import { useState, useRef, useEffect, useCallback, useMemo, use } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  AreaChart, Area, RadarChart, Radar, PolarGrid, PolarAngleAxis,
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, PolarRadiusAxis,
} from "recharts";

import Dashboard, {
  parseHCCBWorkbook,
  parseGenericWorkbook,
  isHCCBWorkbook,
  isDDRWorkbook,
  parseDDRWorkbook,
} from "./Dashboard";

// arrow-js lazy load (degrades gracefully)
let _arrow = null;
let _arrowFailed = false;
function tryLoadArrow() {
  if (_arrow || _arrowFailed) return Promise.resolve(_arrow);
  return import(/* webpackChunkName: "arrowjs" */ "@arrow-js/core")
    .then(mod => { _arrow = { html: mod.html, reactive: mod.reactive }; return _arrow; })
    .catch(() => { _arrowFailed = true; return null; });
}

const API_BASE      = (process.env.REACT_APP_BACKEND || "http://localhost:8000") + "/rag";
const FEEDBACK_BASE = (process.env.REACT_APP_BACKEND || "http://localhost:8000") + "/api/feedback";
const KOINE_BASE    = (process.env.REACT_APP_BACKEND || "http://localhost:8000") + "/api/koine/chat";

// Public-folder preview file (served from /public/preview.xlsx)
const PREVIEW_FILE_URL = (process.env.PUBLIC_URL || "") + "/preview.xlsx";
const PREVIEW_FILE_URL_2 = (process.env.PUBLIC_URL || "") + "/fbo_volume_growth.xlsx";
const PREVIEW_FILE_URL_3 = (process.env.PUBLIC_URL || "") + "/swa_volume_growth.xlsx";
const PREVIEW_FILE_URL_4 = (process.env.PUBLIC_URL || "") + "/ddr_outlets.xlsx";
/* ════════════════════════════════════════════════════════════════════════
   DESIGN TOKENS
   ════════════════════════════════════════════════════════════════════════ */
const T = {
  bg:           "#faf8f3",
  bgAlt:        "#f4f1ea",
  surface:      "#ffffff",
  sidebar:      "#f1ede4",
  sidebarDark:  "#ebe6da",
  border:       "#e4ddce",
  borderStrong: "#c9bfa8",
  text:         "#1c1b18",
  textMid:      "#55524b",
  textDim:      "#8a857a",
  accent:       "#c7432b",
  accentSoft:   "#fbe9e3",
  accentDark:   "#9d3522",
  accentInk:    "#6b2214",
  userBubble:   "#ede7d9",
  code:         "#1a1915",
  codeText:     "#e8e3d5",
  shadow:       "0 1px 2px rgba(60,50,30,0.06), 0 2px 6px rgba(60,50,30,0.04)",
  shadowMd:     "0 4px 14px rgba(60,50,30,0.08), 0 2px 6px rgba(60,50,30,0.05)",
  shadowLg:     "0 20px 50px -20px rgba(60,50,30,0.25), 0 8px 20px rgba(60,50,30,0.08)",
  radius:       14,
  radiusSm:     10,
  radiusLg:     20,
  serif:       "'Geist', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  sans:         "'Geist', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  mono:         "'JetBrains Mono', 'Fira Code', ui-monospace, monospace",
};

const CHART_COLORS = [
  "#c7432b", "#d97a47", "#e8b84e", "#5a8a3a", "#3b7ba6",
  "#7a4b9e", "#2a8a7a", "#b8562a", "#6a6a6a", "#8b2e5e",
];

const CHART_TYPES = [
  { id: "bar",     label: "Bar",     glyph: "▊" },
  { id: "hbar",    label: "H-Bar",   glyph: "▬" },
  { id: "line",    label: "Line",    glyph: "⋰" },
  { id: "area",    label: "Area",    glyph: "◭" },
  { id: "pie",     label: "Pie",     glyph: "◔" },
  { id: "donut",   label: "Donut",   glyph: "◎" },
  { id: "radar",   label: "Radar",   glyph: "⬡" },
  { id: "scatter", label: "Scatter", glyph: "⁙" },
];

const TOOL_OPTIONS = [
  { id: "auto",      label: "Auto",      desc: "Picks best tool for your upload" },
  { id: "vanilla",   label: "Chat",      desc: "Direct conversation, no retrieval" },
  { id: "ai_search", label: "AI Search", desc: "Vector retrieval across your docs" },
  { id: "lightrag",  label: "LightRAG",  desc: "Knowledge graph + entity linking" },
];

const SUPPORTED_EXT = [".xlsx", ".xls", ".xlsm", ".csv", ".pdf", ".docx", ".txt", ".md"];
const SPREADSHEET_EXT = [".xlsx", ".xls", ".xlsm", ".csv"];

const THINKING_QUOTES = [
  "Reading between the lines… hold up, taking a fizz of Thums Up.",
  "Consulting the knowledge graph… adding some Zing with Limca.",
  "Weighing the evidence… things go better with a Coca-Cola.",
  "Drawing connections… Boldly refreshing like a RimZhim masala soda.",
  "Framing an answer worth your time… staying bright and orange with Fanta.",
  "Retrieving the relevant passages… Taste the Thunder in every byte.",
  "Cross-referencing the data… lime-n-lemoni Limca logic applied.",
  "Shaping the response… with the Real Magic of a cold Coke.",
  "Thinking this through carefully… let the Fanta fun flow through the logic.",
  "Separating signal from noise… keeping it spicy and sharp like Thums Up.",
  "Composing a thoughtful reply… brewing the secret formula since 1886.",
  "Checking the facts twice… Refreshment in progress, don't lose the fizz.",
];

/* ════════════════════════════════════════════════════════════════════════
   HELPERS
   ════════════════════════════════════════════════════════════════════════ */
const fmt = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};
const getStoredToken = () => localStorage.getItem("konet_token") || "";
const getStoredUser = () => {
  try { return JSON.parse(localStorage.getItem("konet_user")); } catch { return null; }
};
const clearAuth = () => {
  localStorage.removeItem("konet_token");
  localStorage.removeItem("konet_user");
  localStorage.removeItem("konet_token_expiry");
};

const getFileExt  = (name) => "." + (name?.split(".").pop() || "").toLowerCase();
const isSpreadsheet = (name) => name && SPREADSHEET_EXT.includes(getFileExt(name));

function handleUnauthorized() {
  try {
    localStorage.setItem("login_redirect_to", window.location.pathname + window.location.search);
  } catch {}
  clearAuth();
  setTimeout(() => { window.location.replace("/"); }, 50);
}

async function authedFetch(url, options = {}) {
  const token = getStoredToken();
  const headers = new Headers(options.headers || {});
  if (token && !headers.has("Authorization")) headers.set("Authorization", `Bearer ${token}`);
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401) {
    handleUnauthorized();
    throw new Error("Session expired — redirecting to login");
  }
  return res;
}

function extractChartData(text) {
  try {
    const m = (text || "").match(/```chart-data\n([\s\S]*?)```/);
    if (m) return JSON.parse(m[1]);
  } catch {}
  return null;
}
const stripChartBlock = (t) =>
  (t || "")
    .replace(/```chart-data\n[\s\S]*?```/g, "")
    .replace(/```widget\n[\s\S]*?```/g, "")
    .replace(/<(tool_call|tool_response|function_calls|invoke|parameter)\b[\s\S]*?<\/\1>/gi, "")
    .replace(/<(tool_call|tool_response|function_calls|invoke|parameter)\b[\s\S]*$/i, "")
    .trim();

function detectAmbiguity(text, hasFile) {
  const trimmed = (text || "").trim();
  const wordCount = trimmed.split(/\s+/).filter(Boolean).length;
  const lower = trimmed.toLowerCase();
  if (/^(hi|hey|hello|thanks|thank you|ok|okay|yes|no|yep|nope)[.!?]?$/i.test(trimmed)) return null;
  if (wordCount >= 8) return null;
  if (trimmed.endsWith("?") && wordCount >= 4) return null;

  const vagueTriggers = [
    { re: /^(analyze|analyse|summarize|summarise|review|look at|check)(\s|$)/i,
      question: "What kind of analysis would be most useful?",
      options: [
        { label: "Executive summary", value: "Give me a concise executive summary with key takeaways." },
        { label: "Deep dive with numbers", value: "Do a thorough analysis with specific numbers, statistics, and trends." },
        { label: "Spot anomalies & risks", value: "Identify anomalies, outliers, and potential risks in the data." },
        { label: "Quick bullet overview", value: "Give me a quick bullet-point overview — nothing fancy." },
      ]},
    { re: /\b(help|assist)( me)?\b/i,
      question: "What would you like help with specifically?",
      options: [
        { label: "Explain a concept", value: "Explain a specific concept clearly." },
        { label: "Draft something", value: "Help me draft content." },
        { label: "Compare options", value: "Help me compare options and decide." },
        { label: "Other — I'll specify", value: "__CUSTOM__" },
      ]},
    { re: /\b(chart|graph|visualiz|plot|visual)\w*\b/i,
      question: "What should the visualization focus on?",
      options: [
        { label: "Top values / ranking", value: "Show a chart of the top values ranked highest to lowest." },
        { label: "Trend over time", value: "Show a chart of how values trend over time." },
        { label: "Distribution / share", value: "Show a chart of the distribution or share across categories." },
        { label: "Comparison across groups", value: "Show a chart comparing values across groups or segments." },
      ]},
    { re: /^(what|tell me)( about)?( (it|this|that))?\??$/i,
      question: "Anything specific you'd like to know?",
      options: [
        { label: "Give me the key highlights", value: "Give me the top 5 key highlights." },
        { label: "Explain the main themes", value: "Explain the main themes in the document." },
        { label: "List notable facts", value: "List the most notable facts and figures." },
        { label: "Who/what/when/where summary", value: "Summarize the who, what, when, and where." },
      ]},
  ];

  for (const trig of vagueTriggers) {
    if (trig.re.test(lower)) return { originalQuery: trimmed, question: trig.question, options: trig.options };
  }
  if (wordCount <= 3 && !hasFile && !/\?/.test(trimmed)) {
    return {
      originalQuery: trimmed,
      question: "Could you tell me a little more about what you need?",
      options: [
        { label: `Explain "${trimmed}" in depth`, value: `Explain "${trimmed}" in depth, with examples.` },
        { label: `Give a brief overview of "${trimmed}"`, value: `Give me a short, one-paragraph overview of "${trimmed}".` },
        { label: `Compare "${trimmed}" with alternatives`, value: `Compare "${trimmed}" with common alternatives.` },
        { label: "Something else — I'll specify", value: "__CUSTOM__" },
      ],
    };
  }
  return null;
}

/* ════════════════════════════════════════════════════════════════════════
   SHEETJS LOADER (for dashboard parsing)
   ════════════════════════════════════════════════════════════════════════ */
let _xlsx = null;
let _xlsxLoadingPromise = null;
function loadXLSX() {
  if (_xlsx) return Promise.resolve(_xlsx);
  if (typeof window !== "undefined" && window.XLSX) { _xlsx = window.XLSX; return Promise.resolve(_xlsx); }
  if (_xlsxLoadingPromise) return _xlsxLoadingPromise;
  _xlsxLoadingPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js";
    script.onload = () => { _xlsx = window.XLSX; resolve(_xlsx); };
    script.onerror = () => reject(new Error("Failed to load SheetJS"));
    document.head.appendChild(script);
  });
  return _xlsxLoadingPromise;
}

async function parseSpreadsheetFile(file, sessionId) {
  const XLSX = await loadXLSX();
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });

  if (isDDRWorkbook(wb)) {
    const ddr = parseDDRWorkbook(wb, XLSX);
    if (ddr && ddr.rows.length) return ddr;
  }
  const isHCCB = isHCCBWorkbook(wb);
  const parsed = isHCCB ? parseHCCBWorkbook(wb, XLSX) : parseGenericWorkbook(wb, XLSX);
  if (!parsed || !parsed.cboData || !parsed.cboData.length) {
    throw new Error("Couldn't extract tabular data from this file.");
  }
  return parsed;
}

async function fetchAndParsePreview2() {
  const resp = await fetch(PREVIEW_FILE_URL_2);
  if (!resp.ok) throw new Error(`Sample file not found at ${PREVIEW_FILE_URL_2}`);
  const blob = await resp.blob();
  const file = new File([blob], "fbo_volume_growth.xlsx", { type: blob.type });
  const parsed = await parseSpreadsheetFile(file);
  return { parsed, file };
}


async function fetchAndParsePreview() {
  const resp = await fetch(PREVIEW_FILE_URL);
  if (!resp.ok) throw new Error(`Sample file not found at ${PREVIEW_FILE_URL}`);
  const blob = await resp.blob();
  const file = new File([blob], "preview.xlsx", { type: blob.type });
  const parsed = await parseSpreadsheetFile(file);
  return { parsed, file };
}

/* ════════════════════════════════════════════════════════════════════════
   ICONS
   ════════════════════════════════════════════════════════════════════════ */
const Icon = ({ name, size = 16, color = "currentColor", strokeWidth = 1.7 }) => {
  const p = { width: size, height: size, viewBox: "0 0 24 24", fill: "none",
    stroke: color, strokeWidth, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (name) {
    case "plus":       return <svg {...p}><path d="M12 5v14M5 12h14"/></svg>;
    case "send":       return <svg {...p}><path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z"/></svg>;
    case "mic":        return <svg {...p}><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v3"/></svg>;
    case "micOff":     return <svg {...p}><path d="M1 1l22 22M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6M19 10v2a7 7 0 0 1-.11 1.23M12 19v3"/></svg>;
    case "paperclip":  return <svg {...p}><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>;
    case "x":          return <svg {...p}><path d="M18 6L6 18M6 6l12 12"/></svg>;
    case "file":       return <svg {...p}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>;
    case "trash":      return <svg {...p}><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>;
    case "chat":       return <svg {...p}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>;
    case "logout":     return <svg {...p}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/></svg>;
    case "sparkles":   return <svg {...p}><path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5zM19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8z"/></svg>;
    case "globe":      return <svg {...p}><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15 15 0 0 1 4 10 15 15 0 0 1-4 10 15 15 0 0 1-4-10 15 15 0 0 1 4-10z"/></svg>;
    case "network":    return <svg {...p}><circle cx="12" cy="5" r="2"/><circle cx="5" cy="19" r="2"/><circle cx="19" cy="19" r="2"/><path d="M12 7v3M12 10l-6 7M12 10l6 7"/></svg>;
    case "tree":       return <svg {...p}><circle cx="6" cy="6" r="2"/><circle cx="6" cy="18" r="2"/><circle cx="18" cy="12" r="2"/><path d="M8 6h4a4 4 0 0 1 4 4v0M8 18h4a4 4 0 0 0 4-4v0"/></svg>;
    case "chevDown":   return <svg {...p}><polyline points="6 9 12 15 18 9"/></svg>;
    case "chevUp":     return <svg {...p}><polyline points="18 15 12 9 6 15"/></svg>;
    case "chevRight":  return <svg {...p}><polyline points="9 6 15 12 9 18"/></svg>;
    case "stop":       return <svg {...p}><rect x="6" y="6" width="12" height="12" rx="2"/></svg>;
    case "check":      return <svg {...p}><polyline points="20 6 9 17 4 12"/></svg>;
    case "copy":       return <svg {...p}><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>;
    case "refresh":    return <svg {...p}><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>;
    case "user":       return <svg {...p}><circle cx="12" cy="8" r="4"/><path d="M4 22c0-4 4-6 8-6s8 2 8 6"/></svg>;
    case "thumbUp":    return <svg {...p}><path d="M7 22V11m0 0l5-9 1 1v6h7l-3 13H7"/></svg>;
    case "thumbDown":  return <svg {...p}><path d="M17 2v11m0 0l-5 9-1-1v-6H4l3-13h10"/></svg>;
    case "zap":        return <svg {...p}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>;
    case "layers":     return <svg {...p}><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>;
    case "zoomIn":     return <svg {...p}><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3M11 8v6M8 11h6"/></svg>;
    case "zoomOut":    return <svg {...p}><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3M8 11h6"/></svg>;
    case "sidebar":    return <svg {...p}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18"/></svg>;
    case "grid":       return <svg {...p}><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>;
    case "download":   return <svg {...p}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>;
    case "search":     return <svg {...p}><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg>;
    case "edit":       return <svg {...p}><path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>;
    default: return null;
  }
};

/* ════════════════════════════════════════════════════════════════════════
   LOADERS
   ════════════════════════════════════════════════════════════════════════ */
function ThinkingIndicator() {
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * THINKING_QUOTES.length));
  useEffect(() => {
    const i = setInterval(() => setIdx(() => Math.floor(Math.random() * THINKING_QUOTES.length)), 2500);
    return () => clearInterval(i);
  }, []);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "4px 0" }}>
      <div className="thinking-orb" style={{
        width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
        background: `conic-gradient(from 0deg, ${T.accent}, ${T.accent}, transparent)`,
        animation: "orbSpin 1.2s linear infinite", position: "relative",
      }}>
        <div style={{ position: "absolute", inset: 3, background: T.bg, borderRadius: "50%" }}/>
      </div>
      <span style={{ fontFamily: T.serif, fontStyle: "italic", fontSize: 14.5, color: T.textMid, opacity: 0.92 }}>
        {THINKING_QUOTES[idx]}
      </span>
    </div>
  );
}

function Spinner({ size = 14, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ animation: "spin 0.8s linear infinite" }}>
      <circle cx="12" cy="12" r="9" stroke={color} strokeOpacity="0.25" strokeWidth="2.4" fill="none"/>
      <path d="M12 3a9 9 0 0 1 9 9" stroke={color} strokeWidth="2.4" strokeLinecap="round" fill="none"/>
    </svg>
  );
}

function PulseDots({ color = T.accent }) {
  return (
    <div style={{ display: "inline-flex", gap: 4 }}>
      {[0, 1, 2].map(d => (
        <span key={d} style={{
          width: 6, height: 6, borderRadius: "50%", background: color,
          display: "inline-block",
          animation: `pulse 1.2s ease-in-out ${d * 0.2}s infinite`,
        }}/>
      ))}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   CLARIFIER
   ════════════════════════════════════════════════════════════════════════ */
function Clarifier({ data, onAnswer, onSkip }) {
  const [custom, setCustom] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  return (
    <div style={{
      background: T.surface, border: `1px solid ${T.border}`,
      borderRadius: T.radius, padding: "18px 20px",
      boxShadow: T.shadow, maxWidth: 640, margin: "4px 0",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <div style={{
          width: 28, height: 28, borderRadius: 8, background: T.accentSoft,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Icon name="sparkles" size={14} color={T.accent}/>
        </div>
        <div style={{ fontFamily: T.serif, fontSize: 15.5, fontWeight: 600, color: T.text, letterSpacing: "-0.2px" }}>
          {data.question}
        </div>
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        {data.options.map((opt, i) => {
          const isCustom = opt.value === "__CUSTOM__";
          if (isCustom && showCustom) {
            return (
              <div key={i} style={{
                padding: "10px 12px", background: T.bg,
                border: `1.5px solid ${T.accent}`, borderRadius: T.radiusSm,
                display: "flex", gap: 8,
              }}>
                <input autoFocus value={custom} onChange={e => setCustom(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && custom.trim()) onAnswer(custom.trim()); }}
                  placeholder="Tell me what you're looking for…"
                  style={{
                    flex: 1, border: "none", outline: "none", background: "transparent",
                    fontFamily: T.sans, fontSize: 13.5, color: T.text,
                  }}/>
                <button onClick={() => custom.trim() && onAnswer(custom.trim())}
                  disabled={!custom.trim()}
                  style={{
                    padding: "4px 12px", borderRadius: 6,
                    background: custom.trim() ? T.accent : T.border,
                    color: "#fff", border: "none",
                    cursor: custom.trim() ? "pointer" : "not-allowed",
                    fontSize: 12, fontWeight: 600,
                  }}>Send</button>
              </div>
            );
          }
          return (
            <button key={i} onClick={() => isCustom ? setShowCustom(true) : onAnswer(opt.value)}
              className="clarifier-opt"
              style={{
                textAlign: "left", padding: "10px 14px",
                borderRadius: T.radiusSm, border: `1px solid ${T.border}`,
                background: T.bg, color: T.text,
                fontFamily: T.sans, fontSize: 13.5, fontWeight: 500,
                cursor: "pointer", transition: "all 0.15s",
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
              }}>
              <span>{opt.label}</span>
              <Icon name="chevRight" size={13} color={T.textDim}/>
            </button>
          );
        })}
      </div>
      <div style={{
        marginTop: 12, paddingTop: 10, borderTop: `1px dashed ${T.border}`,
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <span style={{ fontSize: 11.5, color: T.textDim, fontStyle: "italic" }}>
          Original: "{data.originalQuery}"
        </span>
        <button onClick={onSkip} style={{
          background: "none", border: "none", color: T.textMid,
          fontSize: 11.5, cursor: "pointer", textDecoration: "underline",
          textDecorationStyle: "dotted", padding: 0,
        }}>Skip — use my exact question</button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   COMPUTATION PANEL & SOURCES (unchanged)
   ════════════════════════════════════════════════════════════════════════ */
function ComputationPanel({ code, result, error }) {
  const [open, setOpen] = useState(false);
  const hasError = !!error;
  return (
    <div style={{
      marginBottom: 10, borderRadius: T.radiusSm, overflow: "hidden",
      border: `1px solid ${hasError ? "#e8b4a8" : T.border}`,
      background: hasError ? "#fdf4f0" : T.bgAlt,
    }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", display: "flex", alignItems: "center", gap: 8,
        padding: "8px 12px", background: "none", border: "none",
        cursor: "pointer", textAlign: "left",
      }}>
        <span style={{ fontSize: 13 }}>{hasError ? "⚠" : "⚙"}</span>
        <span style={{
          fontSize: 11.5, fontWeight: 600,
          color: hasError ? T.accent : T.textMid, fontFamily: T.mono,
          letterSpacing: "0.3px",
        }}>{hasError ? "computation error" : "computation details"}</span>
        <span style={{ marginLeft: "auto", fontSize: 11, color: T.textDim }}>{open ? "hide" : "show"}</span>
      </button>
      {open && (
        <div style={{ padding: "0 12px 12px", borderTop: `1px solid ${T.border}` }}>
          {code && <>
            <Label>generated code</Label>
            <pre style={codePre}>{code}</pre>
          </>}
          {(result || error) && <>
            <Label>{hasError ? "error output" : "execution output"}</Label>
            <pre style={{ ...codePre, color: hasError ? "#ffb0a0" : "#b5e0a0", maxHeight: 300, overflowY: "auto" }}>
              {result || error}
            </pre>
          </>}
        </div>
      )}
    </div>
  );
}
const Label = ({ children }) => (
  <div style={{
    fontSize: 10, fontWeight: 700, color: T.textDim,
    marginTop: 10, marginBottom: 4, textTransform: "uppercase",
    letterSpacing: "1.2px", fontFamily: T.mono,
  }}>{children}</div>
);
const codePre = {
  background: T.code, color: T.codeText, borderRadius: T.radiusSm,
  padding: "10px 14px", fontSize: 11.5, overflowX: "auto",
  fontFamily: T.mono, margin: 0, lineHeight: 1.6,
};

function SourcesPanel({ sources = [], bingSources = [] }) {
  const [open, setOpen] = useState(false);
  const total = sources.length + bingSources.length;
  if (total === 0) return null;
  return (
    <div style={{
      marginBottom: 10, borderRadius: T.radiusSm, overflow: "hidden",
      border: `1px solid ${T.border}`, background: T.bgAlt,
    }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", display: "flex", alignItems: "center", gap: 8,
        padding: "8px 12px", background: "none", border: "none",
        cursor: "pointer", textAlign: "left",
      }}>
        <Icon name="file" size={13} color={T.textMid}/>
        <span style={{ fontSize: 11.5, fontWeight: 600, color: T.textMid, fontFamily: T.mono, letterSpacing: "0.3px" }}>
          {total} source{total > 1 ? "s" : ""} retrieved
        </span>
        <span style={{ marginLeft: "auto", fontSize: 11, color: T.textDim }}>{open ? "hide" : "show"}</span>
      </button>
      {open && (
        <div style={{ padding: "6px 12px 12px", borderTop: `1px solid ${T.border}` }}>
          {sources.map((s, i) => (
            <div key={s.id || i} style={{
              fontSize: 12.5, color: T.textMid, margin: "8px 0", padding: "9px 11px",
              background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radiusSm,
              lineHeight: 1.55,
            }}>
              <div style={{ fontWeight: 700, color: T.accent, fontSize: 10.5, marginBottom: 4, fontFamily: T.mono }}>
                [{i + 1}] · relevance {s.score}
              </div>
              {s.excerpt}
            </div>
          ))}
          {bingSources.map((c, i) => (
            <a key={`b${i}`} href={c.url} target="_blank" rel="noreferrer" style={{
              display: "block", fontSize: 12, margin: "6px 0",
              padding: "7px 11px", color: T.accent, textDecoration: "none",
              background: T.accentSoft, borderRadius: T.radiusSm, border: `1px solid ${T.border}`,
            }}>🌐 {c.title}</a>
          ))}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   KNOWLEDGE GRAPH
   ════════════════════════════════════════════════════════════════════════ */
function KnowledgeGraph({ data }) {
  const nodes = data?.nodes || [];
  const edges = data?.edges || [];
  const svgRef = useRef(null);
  const [positions, setPositions] = useState([]);
  const [hover, setHover] = useState(null);
  const [drag, setDrag] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({ x: 0, y: 0, px: 0, py: 0 });
  const W = 800, H = 500;

  useEffect(() => {
    if (!nodes.length) return;
    const cx = W / 2, cy = H / 2;
    let pos = nodes.map((n, i) => {
      const a = (2 * Math.PI * i) / nodes.length;
      const r = 160;
      return { ...n, x: cx + r * Math.cos(a) + (Math.random() - 0.5) * 40,
        y: cy + r * Math.sin(a) + (Math.random() - 0.5) * 40, vx: 0, vy: 0 };
    });
    const posById = {};
    pos.forEach(n => posById[n.id] = n);
    for (let iter = 0; iter < 180; iter++) {
      for (let i = 0; i < pos.length; i++) {
        for (let j = i + 1; j < pos.length; j++) {
          const dx = pos[j].x - pos[i].x;
          const dy = pos[j].y - pos[i].y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 1;
          const force = 2500 / (dist * dist);
          const fx = (dx / dist) * force;
          const fy = (dy / dist) * force;
          pos[i].vx -= fx; pos[i].vy -= fy;
          pos[j].vx += fx; pos[j].vy += fy;
        }
      }
      edges.forEach(e => {
        const s = posById[e.source], t = posById[e.target];
        if (!s || !t) return;
        const dx = t.x - s.x, dy = t.y - s.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const force = (dist - 140) * 0.04;
        const fx = (dx / dist) * force, fy = (dy / dist) * force;
        s.vx += fx; s.vy += fy;
        t.vx -= fx; t.vy -= fy;
      });
      pos.forEach(n => { n.vx += (cx - n.x) * 0.003; n.vy += (cy - n.y) * 0.003; });
      pos.forEach(n => {
        n.vx *= 0.85; n.vy *= 0.85;
        n.x += n.vx; n.y += n.vy;
        n.x = Math.max(40, Math.min(W - 40, n.x));
        n.y = Math.max(40, Math.min(H - 40, n.y));
      });
    }
    setPositions(pos);
  }, [nodes, edges]);

  const posMap = useMemo(() => Object.fromEntries(positions.map(n => [n.id, n])), [positions]);
  const typeColor = (type) => ({
    person: "#3b7ba6", org: "#5a8a3a", place: "#d97a47",
    event: "#7a4b9e", concept: T.accent, other: "#8a857a",
  }[type] || T.accent);
  const svgToGraph = (e) => {
    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * W;
    const y = ((e.clientY - rect.top) / rect.height) * H;
    return { x: (x - pan.x) / zoom, y: (y - pan.y) / zoom };
  };
  const onMouseDown = (e, nodeId) => { e.stopPropagation(); setDrag({ id: nodeId, start: svgToGraph(e) }); };
  const onMouseMove = (e) => {
    if (drag) {
      const pt = svgToGraph(e);
      setPositions(prev => prev.map(n => n.id === drag.id ? { ...n, x: pt.x, y: pt.y } : n));
    } else if (isPanning) {
      const rect = svgRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * W;
      const y = ((e.clientY - rect.top) / rect.height) * H;
      setPan({ x: panStart.current.px + (x - panStart.current.x), y: panStart.current.py + (y - panStart.current.y) });
    }
  };
  const onMouseUp = () => { setDrag(null); setIsPanning(false); };
  const onBgDown = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * W;
    const y = ((e.clientY - rect.top) / rect.height) * H;
    panStart.current = { x, y, px: pan.x, py: pan.y };
    setIsPanning(true);
  };

  if (nodes.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: T.textDim, fontSize: 13, fontFamily: T.serif, fontStyle: "italic" }}>
        No entities extracted from this document yet.
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <div style={{
        position: "absolute", top: 10, right: 10, zIndex: 5,
        display: "flex", gap: 4, background: T.surface,
        padding: 4, borderRadius: T.radiusSm, boxShadow: T.shadow,
        border: `1px solid ${T.border}`,
      }}>
        <button onClick={() => setZoom(z => Math.min(3, z * 1.2))} style={toolBtn}><Icon name="zoomIn" size={13}/></button>
        <button onClick={() => setZoom(z => Math.max(0.3, z / 1.2))} style={toolBtn}><Icon name="zoomOut" size={13}/></button>
        <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} style={toolBtn}><Icon name="refresh" size={13}/></button>
      </div>
      <div style={{
        position: "absolute", bottom: 10, left: 10, zIndex: 5,
        background: T.surface, padding: "6px 10px", borderRadius: T.radiusSm,
        border: `1px solid ${T.border}`, fontSize: 10.5, fontFamily: T.mono,
        display: "flex", flexWrap: "wrap", gap: 10, maxWidth: 360,
      }}>
        {["person", "org", "place", "event", "concept"].map(t => (
          <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: typeColor(t) }}/>{t}
          </span>
        ))}
      </div>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} width="100%"
        style={{
          background: `radial-gradient(circle at 30% 20%, ${T.bgAlt} 0%, ${T.bg} 70%)`,
          borderRadius: T.radiusSm, border: `1px solid ${T.border}`,
          cursor: isPanning ? "grabbing" : "grab", display: "block", height: 500,
        }}
        onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp} onMouseDown={onBgDown}>
        <defs>
          <marker id="kg-arrow" markerWidth="7" markerHeight="7" refX="7" refY="3.5" orient="auto">
            <polygon points="0 0, 7 3.5, 0 7" fill={T.borderStrong}/>
          </marker>
        </defs>
        <g transform={`translate(${pan.x},${pan.y}) scale(${zoom})`}>
          {edges.map((e, i) => {
            const s = posMap[e.source], t = posMap[e.target];
            if (!s || !t) return null;
            const mx = (s.x + t.x) / 2, my = (s.y + t.y) / 2;
            const isHover = hover === e.source || hover === e.target;
            return (
              <g key={i} opacity={hover && !isHover ? 0.25 : 1}>
                <line x1={s.x} y1={s.y} x2={t.x} y2={t.y}
                  stroke={isHover ? T.accent : T.borderStrong}
                  strokeWidth={isHover ? 2 : 1.2} markerEnd="url(#kg-arrow)"/>
                {e.label && (
                  <g transform={`translate(${mx},${my})`}>
                    <rect x={-(String(e.label).length * 2.8)} y={-6.5}
                      width={String(e.label).length * 5.6} height={13}
                      rx={3} fill={T.surface} stroke={T.border} strokeWidth="0.5"/>
                    <text fontSize={9} fill={T.textMid} textAnchor="middle" y={3.2} fontFamily={T.mono}>
                      {e.label.slice(0, 20)}
                    </text>
                  </g>
                )}
              </g>
            );
          })}
          {positions.map((n) => {
            const fill = typeColor(n.type);
            const isHover = hover === n.id;
            const labelText = (n.label || n.id).slice(0, 16);
            const rad = 24 + (isHover ? 4 : 0);
            return (
              <g key={n.id} transform={`translate(${n.x},${n.y})`} style={{ cursor: "grab" }}
                onMouseDown={(e) => onMouseDown(e, n.id)}
                onMouseEnter={() => setHover(n.id)} onMouseLeave={() => setHover(null)}
                opacity={hover && !isHover ? 0.55 : 1}>
                <circle r={rad + 4} fill={fill} opacity={0.15}/>
                <circle r={rad} fill={fill} stroke="#fff" strokeWidth={2}/>
                <text y={4} fontSize={10.5} fill="#fff" textAnchor="middle"
                  fontWeight="600" fontFamily={T.sans}
                  style={{ pointerEvents: "none", userSelect: "none" }}>{labelText}</text>
                {isHover && (
                  <text y={rad + 14} fontSize={9.5} fill={T.textMid}
                    textAnchor="middle" fontFamily={T.mono} style={{ pointerEvents: "none" }}>
                    {n.type || "concept"}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      </svg>
      <div style={{ fontSize: 11, color: T.textDim, marginTop: 6, fontStyle: "italic", fontFamily: T.serif }}>
        {nodes.length} nodes · {edges.length} edges · drag nodes · scroll bg to pan
      </div>
    </div>
  );
}
const toolBtn = {
  width: 26, height: 26, borderRadius: 6, border: "none",
  background: "transparent", cursor: "pointer", color: T.textMid,
  display: "flex", alignItems: "center", justifyContent: "center",
};

/* ════════════════════════════════════════════════════════════════════════
   MINDMAP
   ════════════════════════════════════════════════════════════════════════ */
function Mindmap({ data }) {
  const root = data?.root;
  if (!root) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: T.textDim, fontSize: 13, fontFamily: T.serif, fontStyle: "italic" }}>
        No mindmap available for this document.
      </div>
    );
  }
  const Branch = ({ node, depth = 0, parentColor }) => {
    const [open, setOpen] = useState(depth < 2);
    const hasKids = (node.children || []).length > 0;
    const colors = [T.accent, "#d97a47", "#5a8a3a", "#3b7ba6", "#7a4b9e"];
    const myColor = depth === 0 ? T.accent : colors[depth % colors.length];
    return (
      <div style={{ position: "relative", display: "flex", alignItems: "flex-start" }}>
        {depth > 0 && (
          <svg width={28} height={32} style={{ position: "absolute", left: -28, top: 8, overflow: "visible" }}>
            <path d="M0,0 C14,0 14,16 28,16" stroke={parentColor || T.borderStrong}
              strokeWidth="1.5" fill="none" strokeLinecap="round"/>
          </svg>
        )}
        <div>
          <button onClick={() => hasKids && setOpen(o => !o)} style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: depth === 0 ? "10px 18px" : "6px 14px",
            borderRadius: depth === 0 ? 24 : 20,
            background: depth === 0 ? `linear-gradient(135deg, ${T.accent}, ${T.accentDark})`
              : depth === 1 ? T.accentSoft : T.surface,
            color: depth === 0 ? "#fff" : depth === 1 ? T.accent : T.text,
            border: depth === 0 ? "none" : `1.5px solid ${depth === 1 ? T.accent : myColor}`,
            fontFamily: depth === 0 ? T.serif : T.sans,
            fontWeight: depth === 0 ? 600 : depth === 1 ? 600 : 500,
            fontSize: depth === 0 ? 16 : depth === 1 ? 13.5 : 12.5,
            letterSpacing: depth === 0 ? "-0.3px" : "0",
            cursor: hasKids ? "pointer" : "default",
            boxShadow: depth === 0 ? T.shadowMd : "none",
            whiteSpace: "nowrap", marginBottom: 4,
          }}>
            {hasKids && <Icon name={open ? "chevDown" : "chevRight"} size={11}/>}
            {node.name}
            {hasKids && (
              <span style={{
                fontSize: 9.5, padding: "1px 6px", borderRadius: 8,
                background: depth === 0 ? "rgba(255,255,255,0.25)" : T.bg,
                color: depth === 0 ? "#fff" : T.textDim, marginLeft: 2,
              }}>{node.children.length}</span>
            )}
          </button>
          {open && hasKids && (
            <div style={{ marginLeft: 36, marginTop: 4 }}>
              {node.children.map((c, i) => (
                <Branch key={i} node={c} depth={depth + 1} parentColor={myColor}/>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };
  return (
    <div style={{
      padding: "24px 28px",
      background: `linear-gradient(135deg, ${T.bg} 0%, ${T.bgAlt} 100%)`,
      borderRadius: T.radiusSm, minHeight: 300,
      border: `1px solid ${T.border}`, overflowX: "auto",
    }}>
      <Branch node={root}/>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   DATA TABLE
   ════════════════════════════════════════════════════════════════════════ */
function DataTable({ columns, rows }) {
  const [page, setPage] = useState(0);
  const PAGE = 8;
  const total = Math.ceil(rows.length / PAGE);
  const visible = rows.slice(page * PAGE, (page + 1) * PAGE);
  return (
    <div style={{
      marginTop: 10, borderRadius: T.radiusSm, overflow: "hidden",
      border: `1px solid ${T.border}`, boxShadow: T.shadow,
    }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 12.5 }}>
          <thead>
            <tr>{columns.map((c, i) => (
              <th key={i} style={{
                background: T.sidebar, color: T.text, padding: "10px 14px",
                textAlign: "left", fontWeight: 700, whiteSpace: "nowrap",
                fontSize: 11.5, borderBottom: `1px solid ${T.borderStrong}`,
                fontFamily: T.sans, letterSpacing: "0.2px",
              }}>{c}</th>
            ))}</tr>
          </thead>
          <tbody>
            {visible.map((row, r) => (
              <tr key={r} style={{ background: r % 2 === 0 ? T.surface : T.bg }}>
                {row.map((cell, c) => (
                  <td key={c} style={{
                    padding: "8px 14px", borderBottom: `1px solid ${T.border}`,
                    color: T.text, whiteSpace: "nowrap", maxWidth: 240,
                    overflow: "hidden", textOverflow: "ellipsis",
                  }}>{cell ?? "-"}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > 1 && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "7px 14px", background: T.bgAlt, borderTop: `1px solid ${T.border}`,
        }}>
          <span style={{ fontSize: 11, color: T.textDim, fontFamily: T.mono }}>
            {rows.length} rows · page {page + 1}/{total}
          </span>
          <div style={{ display: "flex", gap: 6 }}>
            {["‹", "›"].map((ch, d) => (
              <button key={ch}
                onClick={() => setPage(p => Math.max(0, Math.min(total - 1, p + (d ? 1 : -1))))}
                disabled={d ? page === total - 1 : page === 0}
                style={{
                  padding: "3px 10px", borderRadius: 6,
                  border: `1px solid ${T.border}`, background: T.surface,
                  color: T.accent, cursor: "pointer", fontSize: 12,
                  fontWeight: 600, fontFamily: T.mono,
                }}>{ch}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   MARKDOWN RENDERER
   ════════════════════════════════════════════════════════════════════════ */
function RenderMessage({ text }) {
  const lines = text.split("\n");
  const els = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith("|") && lines[i + 1]?.match(/^\|[-| :]+\|$/)) {
      const headers = line.split("|").filter(Boolean).map(h => h.trim());
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].startsWith("|")) {
        rows.push(lines[i].split("|").filter(Boolean).map(c => c.trim()));
        i++;
      }
      els.push(<DataTable key={`t${i}`} columns={headers} rows={rows}/>);
      continue;
    }
    if (line.startsWith("```")) {
      const lang = line.slice(3).trim();
      i++;
      const code = [];
      while (i < lines.length && !lines[i].startsWith("```")) { code.push(lines[i]); i++; }
      els.push(
        <pre key={`c${i}`} style={{
          background: T.code, color: T.codeText, borderRadius: T.radiusSm,
          padding: "14px 18px", fontSize: 12.5, overflowX: "auto", margin: "10px 0",
          fontFamily: T.mono, lineHeight: 1.6,
        }}>
          {lang && (
            <div style={{
              color: T.textDim, fontSize: 10, marginBottom: 8,
              textTransform: "uppercase", letterSpacing: "1px", fontFamily: T.mono,
            }}>{lang}</div>
          )}
          <code>{code.join("\n")}</code>
        </pre>
      );
      i++; continue;
    }
    if (line.startsWith("### ")) {
      els.push(<h4 key={i} style={{ margin: "16px 0 6px", color: T.text, fontFamily: T.serif, fontSize: 16, fontWeight: 600, letterSpacing: "-0.3px" }}>{line.slice(4)}</h4>);
    } else if (line.startsWith("## ")) {
      els.push(<h3 key={i} style={{ margin: "20px 0 8px", color: T.text, fontFamily: T.serif, fontSize: 19, fontWeight: 600, letterSpacing: "-0.4px" }}>{line.slice(3)}</h3>);
    } else if (line.startsWith("# ")) {
      els.push(<h2 key={i} style={{ margin: "22px 0 10px", color: T.text, fontFamily: T.serif, fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px" }}>{line.slice(2)}</h2>);
    } else if (line.startsWith("- ") || line.startsWith("• ") || /^\d+\.\s/.test(line)) {
      const content = line.replace(/^(-|•|\d+\.)\s/, "");
      const num = line.match(/^(\d+)\.\s/);
      els.push(
        <div key={i} style={{ display: "flex", gap: 10, margin: "4px 0", alignItems: "flex-start" }}>
          <span style={{
            color: T.accent, marginTop: 1, flexShrink: 0,
            fontWeight: num ? 700 : 400, fontSize: num ? 13.5 : 16,
            fontFamily: num ? T.mono : T.sans, minWidth: num ? 18 : 10,
          }}>{num ? `${num[1]}.` : "•"}</span>
          <span style={{ fontSize: 14.5, color: T.text, lineHeight: 1.68, flex: 1, fontFamily: T.sans }}>
            {renderInline(content)}
          </span>
        </div>
      );
    } else if (line.trim().startsWith(">")) {
      els.push(
        <div key={i} style={{
          borderLeft: `3px solid ${T.accent}`, paddingLeft: 14, margin: "10px 0",
          color: T.textMid, fontFamily: T.serif, fontStyle: "italic", fontSize: 14.5, lineHeight: 1.65,
        }}>{renderInline(line.trim().slice(1).trim())}</div>
      );
    } else if (line.match(/^---+$/)) {
      els.push(<hr key={i} style={{ border: "none", borderTop: `1px solid ${T.border}`, margin: "16px 0" }}/>);
    } else if (line.trim() === "") {
      els.push(<div key={i} style={{ height: 8 }}/>);
    } else {
      els.push(<p key={i} style={{ margin: "4px 0", lineHeight: 1.7, fontSize: 14.5, color: T.text, fontFamily: T.sans }}>
        {renderInline(line)}
      </p>);
    }
    i++;
  }
  return <div>{els}</div>;
}

function renderInline(line) {
  const parts = line.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g);
  return parts.map((p, j) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={j} style={{ fontWeight: 700, color: T.text }}>{p.slice(2, -2)}</strong>;
    }
    if (p.startsWith("*") && p.endsWith("*") && p.length > 2 && !p.startsWith("**")) {
      return <em key={j} style={{ fontStyle: "italic" }}>{p.slice(1, -1)}</em>;
    }
    if (p.startsWith("`") && p.endsWith("`")) {
      return <code key={j} style={{
        background: T.sidebar, padding: "2px 7px", borderRadius: 5,
        fontSize: 12.5, fontFamily: T.mono, color: T.accentDark,
        border: `1px solid ${T.border}`,
      }}>{p.slice(1, -1)}</code>;
    }
    const linkMatch = p.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      return (
        <a key={j} href={linkMatch[2]} target="_blank" rel="noreferrer" style={{
          color: T.accent, textDecoration: "underline",
          textDecorationColor: `${T.accent}55`, textUnderlineOffset: "2px",
        }}>{linkMatch[1]}</a>
      );
    }
    return p;
  });
}

/* ════════════════════════════════════════════════════════════════════════
   FEEDBACK WIDGET
   ════════════════════════════════════════════════════════════════════════ */
function FeedbackWidget({ threadId, messageId, userQuestion, assistantResponse, token }) {
  const [state, setState] = useState("idle");
  const [value, setValue] = useState(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const sendFeedback = async (kind, reasonText = "") => {
    setSubmitting(true);
    try {
      await authedFetch(FEEDBACK_BASE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          threadId: threadId || "session", messageId,
          feedback: kind, reason: reasonText,
          userToken: token,
          userQuestion: userQuestion || "",
          assistantResponse: assistantResponse || "",
        }),
      });
    } catch {}
    setSubmitting(false);
    setState("sent");
  };

  const handleClick = (kind) => {
    setValue(kind);
    if (kind === "positive") sendFeedback("positive");
    else setState("asking");
  };

  if (state === "sent") {
    return (
      <span style={{ fontSize: 11, color: T.textDim, padding: "4px 8px", fontFamily: T.serif, fontStyle: "italic" }}>
        {value === "positive" ? "✓ Thanks for the feedback" : "✓ Feedback noted — we'll improve"}
      </span>
    );
  }

  if (state === "asking") {
    const quickReasons = ["Not accurate", "Too vague", "Missed the point", "Too long", "Formatting issues"];
    return (
      <div style={{
        display: "inline-flex", flexWrap: "wrap", alignItems: "center",
        gap: 6, padding: "6px 10px", background: T.bgAlt,
        border: `1px solid ${T.border}`, borderRadius: T.radiusSm, marginTop: 4,
      }}>
        <span style={{ fontSize: 11.5, color: T.textMid, fontFamily: T.sans }}>What went wrong?</span>
        {quickReasons.map(r => (
          <button key={r} onClick={() => sendFeedback("negative", r)} disabled={submitting}
            style={{
              padding: "3px 9px", fontSize: 11, borderRadius: 12,
              background: T.surface, border: `1px solid ${T.border}`,
              color: T.textMid, cursor: submitting ? "wait" : "pointer", fontFamily: T.sans,
            }}>{r}</button>
        ))}
        <input value={reason} onChange={e => setReason(e.target.value)}
          placeholder="Other…"
          onKeyDown={e => { if (e.key === "Enter" && reason.trim()) sendFeedback("negative", reason.trim()); }}
          style={{
            border: `1px solid ${T.border}`, borderRadius: 12, padding: "3px 10px",
            fontSize: 11, outline: "none", fontFamily: T.sans,
            background: T.surface, color: T.text, minWidth: 120,
          }}/>
        <button onClick={() => setState("idle")} style={{
          background: "none", border: "none", color: T.textDim,
          fontSize: 11, cursor: "pointer", padding: "2px 6px",
        }}>cancel</button>
      </div>
    );
  }

  return (
    <div style={{ display: "inline-flex", gap: 2 }}>
      <button onClick={() => handleClick("positive")} title="Helpful" disabled={submitting} style={fbBtn}
        onMouseEnter={e => { e.currentTarget.style.background = T.accentSoft; e.currentTarget.style.color = T.accent; }}
        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.textDim; }}>
        <Icon name="thumbUp" size={12}/>
      </button>
      <button onClick={() => handleClick("negative")} title="Not helpful" disabled={submitting} style={fbBtn}
        onMouseEnter={e => { e.currentTarget.style.background = T.accentSoft; e.currentTarget.style.color = T.accent; }}
        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.textDim; }}>
        <Icon name="thumbDown" size={12}/>
      </button>
    </div>
  );
}
const fbBtn = {
  padding: "4px 7px", borderRadius: 6, border: "none",
  background: "transparent", cursor: "pointer", color: T.textDim,
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  transition: "all 0.15s",
};

/* ════════════════════════════════════════════════════════════════════════
   EMPTY STATE
   ════════════════════════════════════════════════════════════════════════ */
function EmptyState({ userName, onSuggestion, hasSession }) {
  const suggestions = hasSession
    ? [
        { icon: "layers", text: "Summarize the document" },
        { icon: "zap", text: "What are the key insights?" },
        { icon: "network", text: "Show me a knowledge graph" },
        { icon: "search", text: "Explain this in simple terms" },
      ]
    : [
        { icon: "sparkles", text: "Explain quantum computing simply" },
        { icon: "edit", text: "Help me draft a product launch plan" },
        { icon: "layers", text: "Compare React vs Vue for a startup" },
        { icon: "zap", text: "Give me a 30-day fitness routine" },
      ];
  const firstName = userName?.split(" ")[0];
  return (
    <div style={{
      flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", padding: "40px 20px", maxWidth: 780, width: "100%",
      margin: "0 auto", minHeight: "60vh",
    }}>
      <div className="hero-mark" style={{
        width: 64, height: 64, borderRadius: 20, marginBottom: 24,
        background: `linear-gradient(135deg, ${T.accent}, ${T.accentDark} 70%, ${T.accentInk})`,
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: `${T.shadowLg}, inset 0 1px 0 rgba(255,255,255,0.2)`,
        position: "relative",
      }}>
        <Icon name="sparkles" size={28} color="#fff"/>
      </div>
      <h1 style={{
        fontFamily: T.serif, fontSize: "clamp(28px, 5vw, 38px)", fontWeight: 600,
        color: T.text, margin: "0 0 8px", textAlign: "center",
        letterSpacing: "-1px", lineHeight: 1.15,
      }}>
        {firstName ? (
          <>Good to see you, <em style={{ fontStyle: "italic", color: T.accent }}>{firstName}</em>.</>
        ) : "How can I help today?"}
      </h1>
      <p style={{
        fontFamily: T.serif, fontSize: 17, color: T.textMid,
        margin: "0 0 40px", textAlign: "center", fontStyle: "italic", letterSpacing: "-0.2px",
      }}>
        {hasSession ? "Ask anything about your document." : "Chat, upload a document, or try one of these —"}
      </p>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
        gap: 12, width: "100%", maxWidth: 640,
      }}>
        {suggestions.map((s, i) => (
          <button key={i} onClick={() => onSuggestion(s.text)} className="suggest-card"
            style={{
              textAlign: "left", padding: "16px 18px",
              borderRadius: T.radius, border: `1px solid ${T.border}`,
              background: T.surface, color: T.text,
              cursor: "pointer", fontSize: 14, lineHeight: 1.5,
              transition: "all 0.2s", fontFamily: T.sans,
              display: "flex", alignItems: "center", gap: 12,
              animation: `fadeUp 0.5s ${0.1 + i * 0.08}s backwards`,
            }}>
            <div style={{
              width: 34, height: 34, borderRadius: 10, flexShrink: 0,
              background: T.accentSoft, color: T.accent,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Icon name={s.icon} size={16} color={T.accent}/>
            </div>
            <span>{s.text}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   DASHBOARD EMPTY STATE — shown when user clicks "Dashboard" but no file
   ════════════════════════════════════════════════════════════════════════ */
function DashboardEmptyState({ onLoadSample, onLoadSample2, onLoadSample3, onLoadSample4, onUpload, loading, error }) {
  return (
    <div style={{
      flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", padding: "40px 20px", minHeight: "70vh",
      background: T.bg,
    }}>
      <div style={{
        width: 72, height: 72, borderRadius: 22, marginBottom: 24,
        background: `linear-gradient(135deg, #E8001A, #B50015 70%, #800010)`,
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 12px 30px -8px rgba(232,0,26,0.4)",
      }}>
        <Icon name="grid" size={32} color="#fff"/>
      </div>
      <h2 style={{
        fontFamily: T.serif, fontSize: 30, fontWeight: 600, color: T.text,
        margin: "0 0 8px", textAlign: "center", letterSpacing: "-0.8px",
      }}>
        Build a Dashboard
      </h2>
      <p style={{
        fontFamily: T.serif, fontSize: 15, color: T.textMid, fontStyle: "italic",
        margin: "0 0 32px", textAlign: "center", maxWidth: 500, lineHeight: 1.55,
      }}>
        Upload a spreadsheet (Excel or CSV) or load the bundled sample dataset
        to spin up an interactive dashboard with KPIs, charts, and AI insights.
      </p>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
        gap: 14, width: "100%", maxWidth: 580,
      }}>
        <button onClick={onLoadSample} disabled={loading} className="suggest-card"
          style={{
            textAlign: "left", padding: "18px 20px",
            borderRadius: T.radius, border: `1.5px solid #E8001A`,
            background: "linear-gradient(135deg, #FFF5F5, #FFFFFF)",
            color: T.text, cursor: loading ? "wait" : "pointer", fontFamily: T.sans,
            display: "flex", flexDirection: "column", gap: 8,
            opacity: loading ? 0.6 : 1, transition: "all 0.2s",
          }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: "#E8001A", color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {loading ? <Spinner size={16} color="#fff"/> : <Icon name="sparkles" size={16} color="#fff"/>}
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>Load Sample</div>
              <div style={{ fontSize: 11, color: T.textDim, fontFamily: T.mono }}>preview.xlsx</div>
            </div>
          </div>
          <div style={{ fontSize: 12.5, color: T.textMid, lineHeight: 1.5 }}>
            HCCB+ volume & growth snapshot — fully populated dashboard with KPIs, heatmap, trends.
          </div>
        </button>

        <button onClick={onLoadSample2} disabled={loading} className="suggest-card"
          style={{
            textAlign: "left", padding: "18px 20px",
            borderRadius: T.radius, border: `1.5px solid #E8001A`,
            background: "linear-gradient(135deg, #FFF5F5, #FFFFFF)",
            color: T.text, cursor: loading ? "wait" : "pointer", fontFamily: T.sans,
            display: "flex", flexDirection: "column", gap: 8,
            opacity: loading ? 0.6 : 1, transition: "all 0.2s",
          }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: "#E8001A", color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {loading ? <Spinner size={16} color="#fff"/> : <Icon name="sparkles" size={16} color="#fff"/>}
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>Load FBO Volume & Growth Sheet</div>
              <div style={{ fontSize: 11, color: T.textDim, fontFamily: T.mono }}>fbo_volume_growth.xlsx</div>
            </div>
          </div>
          <div style={{ fontSize: 12.5, color: T.textMid, lineHeight: 1.5 }}>
           FBO volume & growth snapshot — fully populated dashboard with KPIs, heatmap, trends.
          </div>
        </button>

    <button onClick={onLoadSample3} disabled={loading} className="suggest-card"
          style={{
            textAlign: "left", padding: "18px 20px",
            borderRadius: T.radius, border: `1.5px solid #E8001A`,
            background: "linear-gradient(135deg, #FFF5F5, #FFFFFF)",
            color: T.text, cursor: loading ? "wait" : "pointer", fontFamily: T.sans,
            display: "flex", flexDirection: "column", gap: 8,
            opacity: loading ? 0.6 : 1, transition: "all 0.2s",
          }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: "#E8001A", color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {loading ? <Spinner size={16} color="#fff"/> : <Icon name="sparkles" size={16} color="#fff"/>}
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>Load SWA Volume & Growth Sheet</div>
              <div style={{ fontSize: 11, color: T.textDim, fontFamily: T.mono }}>swa_volume_growth.xlsx</div>
            </div>
          </div>
          <div style={{ fontSize: 12.5, color: T.textMid, lineHeight: 1.5 }}>
           SWA volume & growth snapshot — fully populated dashboard with KPIs, heatmap, trends.
          </div>
        </button>
        <button onClick={onLoadSample4} disabled={loading} className="suggest-card"
  style={{
    textAlign: "left", padding: "18px 20px",
    borderRadius: T.radius, border: `1.5px solid #E8001A`,
    background: "linear-gradient(135deg, #FFF5F5, #FFFFFF)",
    color: T.text, cursor: loading ? "wait" : "pointer", fontFamily: T.sans,
    display: "flex", flexDirection: "column", gap: 8,
    opacity: loading ? 0.6 : 1, transition: "all 0.2s",
  }}>
  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    <div style={{
      width: 36, height: 36, borderRadius: 10,
      background: "#E8001A", color: "#fff",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      {loading ? <Spinner size={16} color="#fff"/> : <Icon name="grid" size={16} color="#fff"/>}
    </div>
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>Load Affordability Acceleration</div>
      <div style={{ fontSize: 11, color: T.textDim, fontFamily: T.mono }}>ddr_outlets.xlsx</div>
    </div>
  </div>
  <div style={{ fontSize: 12.5, color: T.textMid, lineHeight: 1.5 }}>
    DDR-level outlet & Campa penetration — map heatmap, stacked coverage, competitor trends.
  </div>
</button>
        <button onClick={onUpload} className="suggest-card"
          style={{
            textAlign: "left", padding: "18px 20px",
            borderRadius: T.radius, border: `1.5px solid ${T.border}`,
            background: T.surface, color: T.text, cursor: "pointer", fontFamily: T.sans,
            display: "flex", flexDirection: "column", gap: 8, transition: "all 0.2s",
          }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: T.accentSoft, color: T.accent,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Icon name="paperclip" size={16} color={T.accent}/>
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>Upload Your File</div>
              <div style={{ fontSize: 11, color: T.textDim, fontFamily: T.mono }}>.xlsx · .xls · .csv</div>
            </div>
          </div>
          <div style={{ fontSize: 12.5, color: T.textMid, lineHeight: 1.5 }}>
            Auto-detects HCCB format or builds an intelligent generic dashboard from any tabular file.
          </div>
        </button>
      </div>

      <div style={{
        marginTop: 18, display: "flex", gap: 16, alignItems: "center",
        fontSize: 12, color: T.textDim,
      }}>
        <a href={PREVIEW_FILE_URL} download
          style={{ color: T.accent, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 5, fontWeight: 600 }}>
          <Icon name="download" size={12}/> Download sample file
        </a>
        <span style={{ color: T.border }}>·</span>
        <span style={{ fontStyle: "italic", fontFamily: T.serif }}>
          Q&A uses the same KOgnos chat — pops out in a side drawer
        </span>
      </div>

      {error && (
        <div style={{
          marginTop: 20, padding: "10px 16px",
          background: "#fdf0ea", border: `1px solid ${T.accent}`, borderRadius: T.radiusSm,
          fontSize: 12.5, color: T.accentDark, maxWidth: 500, textAlign: "center",
        }}>⚠ {error}</div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   TOOLBOX
   ════════════════════════════════════════════════════════════════════════ */
function Toolbox({ tool, setTool, bing, setBing, koine, setKoine }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const active = TOOL_OPTIONS.find(t => t.id === tool) || TOOL_OPTIONS[0];
  const othersDisabled = koine;
  return (
    <div ref={ref} style={{ position: "relative", display: "flex", gap: 6, flexWrap: "wrap" }}>
      <button onClick={() => !othersDisabled && setOpen(o => !o)} disabled={othersDisabled}
        style={{ ...pillBtn(false), ...(othersDisabled && { opacity: 0.4, cursor: "not-allowed" }) }}>
        <Icon name="sparkles" size={12} />
        {active.label}
        <Icon name={open ? "chevUp" : "chevDown"} size={11} />
      </button>
      <button onClick={() => !othersDisabled && setBing(b => !b)} disabled={othersDisabled} title="Bing web grounding"
        style={{ ...pillBtn(bing && !othersDisabled), ...(othersDisabled && { opacity: 0.4, cursor: "not-allowed" }) }}>
        <Icon name="globe" size={12} />
        Web {bing && !othersDisabled ? "on" : "off"}
      </button>
      <button onClick={() => setKoine(k => !k)} title="Route through Koine" style={pillBtn(koine)}>
        <Icon name="zap" size={12} />
        Koine {koine ? "on" : "off"}
      </button>
      {open && !othersDisabled && (
        <div style={{
          position: "absolute", bottom: "calc(100% + 8px)", left: 0, zIndex: 20,
          background: T.surface, border: `1px solid ${T.border}`,
          borderRadius: T.radius, boxShadow: T.shadowLg,
          minWidth: 280, padding: 6, animation: "fadeUp 0.18s",
        }}>
          {TOOL_OPTIONS.map(t => (
            <div key={t.id} onClick={() => { setTool(t.id); setOpen(false); }} className="tool-opt"
              style={{
                padding: "9px 12px", borderRadius: T.radiusSm, cursor: "pointer",
                background: tool === t.id ? T.accentSoft : "transparent", transition: "background 0.1s",
              }}>
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                fontSize: 13.5, fontWeight: 600, fontFamily: T.sans,
                color: tool === t.id ? T.accent : T.text,
              }}>
                {t.label}
                {tool === t.id && <Icon name="check" size={12} color={T.accent} />}
              </div>
              <div style={{
                fontSize: 11.5, color: T.textDim, marginTop: 2,
                fontFamily: T.serif, fontStyle: "italic",
              }}>{t.desc}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
const pillBtn = (active) => ({
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "6px 12px", borderRadius: 20,
  border: `1px solid ${active ? T.accent : T.border}`,
  background: active ? T.accentSoft : T.surface,
  color: active ? T.accent : T.textMid,
  fontSize: 12, fontWeight: 600, cursor: "pointer",
  fontFamily: T.sans, transition: "all 0.15s",
});

/* ════════════════════════════════════════════════════════════════════════
   VIZ DRAWER (knowledge graph / mindmap)
   ════════════════════════════════════════════════════════════════════════ */
function VizDrawer({ kg, mindmap, onClose, loading }) {
  const [tab, setTab] = useState("kg");
  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(30,25,20,0.4)",
        zIndex: 49, backdropFilter: "blur(2px)", animation: "fadeIn 0.2s",
      }}/>
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: "min(760px, 94vw)",
        background: T.surface, borderLeft: `1px solid ${T.border}`,
        boxShadow: "-16px 0 40px rgba(60,50,30,0.15)", zIndex: 50,
        display: "flex", flexDirection: "column", animation: "slideInRight 0.25s",
      }}>
        <div style={{
          padding: "16px 22px", borderBottom: `1px solid ${T.border}`,
          display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
          background: `linear-gradient(135deg, ${T.bgAlt}, ${T.sidebar})`,
        }}>
          <div>
            <div style={{ fontFamily: T.serif, fontWeight: 600, fontSize: 17, color: T.text, letterSpacing: "-0.3px" }}>
              Document Insight
            </div>
            <div style={{ fontFamily: T.serif, fontStyle: "italic", fontSize: 12, color: T.textMid }}>
              Entities, relations & themes extracted
            </div>
          </div>
          <div style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
            {[["kg", "Knowledge Graph", "network"], ["mm", "Mindmap", "tree"]].map(([id, lbl, ic]) => (
              <button key={id} onClick={() => setTab(id)} style={{
                padding: "6px 14px", borderRadius: 20, fontSize: 12.5, fontWeight: 600,
                border: `1px solid ${tab === id ? T.accent : T.border}`,
                background: tab === id ? T.accent : T.surface,
                color: tab === id ? "#fff" : T.textMid, cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 6, fontFamily: T.sans,
              }}>
                <Icon name={ic} size={12}/> {lbl}
              </button>
            ))}
          </div>
          <button onClick={onClose} style={{
            width: 34, height: 34, borderRadius: 10,
            background: T.surface, border: `1px solid ${T.border}`, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}><Icon name="x" size={14} color={T.textMid}/></button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
          {loading ? (
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              justifyContent: "center", height: "100%", gap: 16,
            }}>
              <Spinner size={32} color={T.accent}/>
              <div style={{ fontFamily: T.serif, fontStyle: "italic", fontSize: 15, color: T.textMid }}>
                Extracting entities and relations…
              </div>
            </div>
          ) : tab === "kg" ? <KnowledgeGraph data={kg}/> : <Mindmap data={mindmap}/>}
        </div>
      </div>
    </>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   MESSAGE ROW

/* ════════════════════════════════════════════════════════════════════════
   STAT CARDS + ADAPTIVE WIDGET (heuristic detection from prose)
   ════════════════════════════════════════════════════════════════════════ */
function extractStatCards(text) {
  const cards = [];
  const lines = text.split("\n").slice(0, 12);
  for (const line of lines) {
    const m = line.match(/^[-•\s]*\*\*([^*]+)\*\*\s*[:：]\s*([^\n]+)$/);
    if (m) {
      const label = m[1].trim();
      const val = m[2].trim().replace(/^`|`$/g, "");
      if (val.length < 40 && !/[.!?]{2,}/.test(val)) cards.push({ label, value: val });
    }
    if (cards.length >= 4) break;
  }
  return cards.length >= 2 ? cards : null;
}

function StatCards({ cards }) {
  const accents = [T.accent, "#d97a47", "#5a8a3a", "#3b7ba6"];
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: `repeat(auto-fit, minmax(${cards.length >= 3 ? 140 : 180}px, 1fr))`,
      gap: 10, margin: "12px 0",
    }}>
      {cards.map((c, i) => (
        <div key={i} className="stat-card" style={{
          padding: "14px 16px", background: T.surface,
          border: `1px solid ${T.border}`,
          borderLeft: `3px solid ${accents[i % accents.length]}`,
          borderRadius: T.radiusSm, boxShadow: T.shadow,
          animation: `fadeSlide 0.5s ${i * 0.08}s backwards`,
        }}>
          <div style={{
            fontSize: 10.5, color: T.textDim, fontFamily: T.mono,
            textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6,
          }}>{c.label}</div>
          <div style={{
            fontFamily: T.serif, fontSize: 22, fontWeight: 600, color: T.text,
            lineHeight: 1.1, letterSpacing: "-0.5px",
          }}>{c.value}</div>
        </div>
      ))}
    </div>
  );
}

function extractWidgetSpec(text) {
  if (!text) return null;
  const fenced = text.match(/```widget\n([\s\S]*?)```/);
  if (fenced) {
    try {
      const spec = JSON.parse(fenced[1]);
      if (spec && spec.type && Array.isArray(spec.items)) return spec;
    } catch {}
  }
  const prosConsRe = /###\s*(?:✓\s*)?Pros\s*\n([\s\S]*?)\n###\s*(?:✗\s*)?Cons\s*\n([\s\S]*?)(?=\n###|\n##|$)/i;
  const pcm = text.match(prosConsRe);
  if (pcm) {
    const pull = (block) => block.split("\n").map((l) => l.replace(/^[-•*]\s+/, "").trim()).filter(Boolean).slice(0, 8);
    const pros = pull(pcm[1]); const cons = pull(pcm[2]);
    if (pros.length && cons.length) return { type: "proscons", items: [{ pros, cons }] };
  }
  const stepLines = text.split("\n").filter((l) => /^\s*\d+\.\s+\S/.test(l));
  if (stepLines.length >= 3 && stepLines.length <= 10) {
    const items = stepLines.slice(0, 8).map((l) => {
      const m = l.match(/^\s*(\d+)\.\s+(.+)$/);
      return m ? { n: m[1], text: m[2].replace(/\*\*/g, "") } : null;
    }).filter(Boolean);
    if (items.length >= 3) return { type: "steps", items };
  }
  const tlMatches = [...text.matchAll(/^\s*[-•]?\s*(\d{4}(?:[-–]\d{2,4})?)\s*[:\-–—]\s*(.+)$/gm)];
  if (tlMatches.length >= 3) {
    const items = tlMatches.slice(0, 10).map((m) => ({ when: m[1], text: m[2].trim().replace(/\*\*/g, "") }));
    return { type: "timeline", items };
  }
  return null;
}

function ReactStepsWidget({ items }) {
  const [active, setActive] = useState(0);
  return (
    <div style={{ margin: "10px 0", fontFamily: T.sans }}>
      {items.map((it, i) => (
        <div key={i} onClick={() => setActive(i)} style={{
          display: "flex", gap: 12, padding: "10px 14px", marginBottom: 6,
          background: i === active ? T.accentSoft : T.surface,
          border: `1px solid ${i === active ? T.accent : T.border}`,
          borderRadius: T.radiusSm, cursor: "pointer", transition: "all 0.15s",
          animation: `fadeSlide 0.3s ${i * 0.05}s backwards`,
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
            background: i === active ? T.accent : T.bgAlt,
            color: i === active ? "#fff" : T.textMid,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: T.mono, fontSize: 12, fontWeight: 700,
          }}>{it.n}</div>
          <div style={{ fontSize: 14, color: T.text, lineHeight: 1.55, paddingTop: 4, flex: 1 }}>{it.text}</div>
        </div>
      ))}
    </div>
  );
}

function AdaptiveWidget({ spec }) {
  if (!spec) return null;
  const labels = { timeline: "Timeline", proscons: "Pros & Cons", steps: "Steps", keyvalues: "Overview", comparison: "Comparison" };
  let body = null;
  if (spec.type === "timeline") {
    body = (
      <div style={{ position: "relative", padding: "8px 0 8px 28px", fontFamily: T.sans }}>
        <div style={{ position: "absolute", left: 9, top: 14, bottom: 14, width: 2, background: `linear-gradient(180deg,${T.accent},${T.border})` }}/>
        {(spec.items || []).map((it, i) => (
          <div key={i} style={{ position: "relative", marginBottom: 14, animation: `fadeSlide 0.4s ${i * 0.08}s backwards` }}>
            <div style={{ position: "absolute", left: -26, top: 4, width: 14, height: 14, borderRadius: "50%", background: T.surface, border: `2px solid ${T.accent}`, boxShadow: `0 0 0 3px ${T.accentSoft}` }}/>
            <div style={{ fontFamily: T.mono, fontSize: 11, color: T.accent, fontWeight: 600, letterSpacing: "0.3px", marginBottom: 2 }}>{it.when}</div>
            <div style={{ fontSize: 14, color: T.text, lineHeight: 1.55 }}>{it.text}</div>
          </div>
        ))}
      </div>
    );
  } else if (spec.type === "proscons") {
    const item = (spec.items || [{}])[0] || {};
    const pros = item.pros || []; const cons = item.cons || [];
    body = (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 12, margin: "10px 0", fontFamily: T.sans }}>
        <div style={{ padding: "14px 16px", background: T.surface, border: `1px solid ${T.border}`, borderLeft: "3px solid #5a8a3a", borderRadius: T.radiusSm }}>
          <div style={{ fontFamily: T.mono, fontSize: 11, color: "#5a8a3a", fontWeight: 700, letterSpacing: "0.5px", marginBottom: 8, textTransform: "uppercase" }}>✓ Pros</div>
          {pros.map((p, i) => <div key={i} style={{ display: "flex", gap: 8, margin: "4px 0", fontSize: 13.5, lineHeight: 1.55, color: T.text }}><span style={{ color: "#5a8a3a", flexShrink: 0 }}>+</span><span>{p}</span></div>)}
        </div>
        <div style={{ padding: "14px 16px", background: T.surface, border: `1px solid ${T.border}`, borderLeft: `3px solid ${T.accent}`, borderRadius: T.radiusSm }}>
          <div style={{ fontFamily: T.mono, fontSize: 11, color: T.accent, fontWeight: 700, letterSpacing: "0.5px", marginBottom: 8, textTransform: "uppercase" }}>✗ Cons</div>
          {cons.map((c, i) => <div key={i} style={{ display: "flex", gap: 8, margin: "4px 0", fontSize: 13.5, lineHeight: 1.55, color: T.text }}><span style={{ color: T.accent, flexShrink: 0 }}>−</span><span>{c}</span></div>)}
        </div>
      </div>
    );
  } else if (spec.type === "steps") {
    body = <ReactStepsWidget items={spec.items || []}/>;
  } else {
    return null;
  }
  return (
    <div style={{
      marginTop: 12, background: T.surface, border: `1px solid ${T.border}`,
      borderRadius: T.radius, overflow: "hidden", boxShadow: T.shadow,
    }}>
      <div style={{
        padding: "8px 14px", background: `linear-gradient(135deg, ${T.bgAlt}, ${T.sidebar})`,
        borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 8,
      }}>
        <span style={{ fontSize: 13 }}>
          {spec.type === "timeline" ? "📅" : spec.type === "proscons" ? "⚖" : spec.type === "steps" ? "🧭" : "✦"}
        </span>
        <span style={{ fontFamily: T.serif, fontSize: 13, fontWeight: 600, color: T.text, letterSpacing: "-0.1px" }}>
          {spec.title || labels[spec.type] || "Widget"}
        </span>
        <span style={{ marginLeft: "auto", fontSize: 10, color: T.textDim, fontFamily: T.mono, letterSpacing: "0.3px" }}>
          react · static
        </span>
      </div>
      <div style={{ padding: "14px 16px" }}>{body}</div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   INLINE CHART
   ════════════════════════════════════════════════════════════════════════ */
function InlineChart({ chartData }) {
  const [activeType, setActiveType] = useState(chartData.suggestedType || "bar");
  const [xKey, setXKey] = useState(chartData.xKey || chartData.columns?.[0] || "");
  const [yKey, setYKey] = useState(chartData.yKey || chartData.columns?.[1] || "");
  const [title, setTitle] = useState(chartData.title || "Chart");
  const [collapsed, setCollapsed] = useState(false);
  const data = chartData.data || [];
  const columns = chartData.columns || [];
  const TT = { background: "#1a1915", border: `1px solid ${T.borderStrong}`, borderRadius: T.radiusSm, color: "#fff", fontSize: 12, fontFamily: T.sans };
  const CM = { data, margin: { top: 10, right: 20, left: 0, bottom: 44 } };
  const render = () => {
    switch (activeType) {
      case "bar": return (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart {...CM}>
            <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
            <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: T.textMid }} angle={-35} textAnchor="end" interval={0}/>
            <YAxis tick={{ fontSize: 11, fill: T.textMid }}/>
            <Tooltip contentStyle={TT}/><Legend/>
            <Bar dataKey={yKey} radius={[4, 4, 0, 0]}>
              {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]}/>)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
      case "hbar": return (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart {...CM} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
            <XAxis type="number" tick={{ fontSize: 11, fill: T.textMid }}/>
            <YAxis dataKey={xKey} type="category" tick={{ fontSize: 11, fill: T.textMid }} width={120}/>
            <Tooltip contentStyle={TT}/>
            <Bar dataKey={yKey} radius={[0, 4, 4, 0]}>
              {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]}/>)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
      case "line": return (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart {...CM}>
            <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
            <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: T.textMid }} angle={-35} textAnchor="end" interval={0}/>
            <YAxis tick={{ fontSize: 11, fill: T.textMid }}/>
            <Tooltip contentStyle={TT}/><Legend/>
            <Line type="monotone" dataKey={yKey} stroke={T.accent} strokeWidth={2.5} dot={{ fill: T.accent, r: 4 }} activeDot={{ r: 6 }}/>
          </LineChart>
        </ResponsiveContainer>
      );
      case "area": return (
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart {...CM}>
            <defs>
              <linearGradient id="ag" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={T.accent} stopOpacity={0.35}/>
                <stop offset="95%" stopColor={T.accent} stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
            <XAxis dataKey={xKey} tick={{ fontSize: 11, fill: T.textMid }} angle={-35} textAnchor="end" interval={0}/>
            <YAxis tick={{ fontSize: 11, fill: T.textMid }}/>
            <Tooltip contentStyle={TT}/>
            <Area type="monotone" dataKey={yKey} stroke={T.accent} strokeWidth={2} fill="url(#ag)"/>
          </AreaChart>
        </ResponsiveContainer>
      );
      case "pie": return (
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie data={data} dataKey={yKey} nameKey={xKey} cx="50%" cy="50%" outerRadius={110}
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
              {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]}/>)}
            </Pie>
            <Tooltip contentStyle={TT}/><Legend/>
          </PieChart>
        </ResponsiveContainer>
      );
      case "donut": return (
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie data={data} dataKey={yKey} nameKey={xKey} cx="50%" cy="50%" innerRadius={70} outerRadius={110} paddingAngle={3}>
              {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]}/>)}
            </Pie>
            <Tooltip contentStyle={TT}/><Legend/>
          </PieChart>
        </ResponsiveContainer>
      );
      case "radar": return (
        <ResponsiveContainer width="100%" height={300}>
          <RadarChart data={data} cx="50%" cy="50%" outerRadius={110}>
            <PolarGrid stroke={T.border}/>
            <PolarAngleAxis dataKey={xKey} tick={{ fontSize: 11, fill: T.textMid }}/>
            <PolarRadiusAxis tick={{ fontSize: 10, fill: T.textDim }}/>
            <Radar dataKey={yKey} stroke={T.accent} fill={T.accent} fillOpacity={0.25} strokeWidth={2}/>
            <Tooltip contentStyle={TT}/>
          </RadarChart>
        </ResponsiveContainer>
      );
      case "scatter": return (
        <ResponsiveContainer width="100%" height={300}>
          <ScatterChart margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={T.border}/>
            <XAxis dataKey={xKey} name={xKey} tick={{ fontSize: 11, fill: T.textMid }}/>
            <YAxis dataKey={yKey} name={yKey} tick={{ fontSize: 11, fill: T.textMid }}/>
            <Tooltip contentStyle={TT} cursor={{ strokeDasharray: "3 3" }}/>
            <Scatter data={data} fill={T.accent} fillOpacity={0.7}/>
          </ScatterChart>
        </ResponsiveContainer>
      );
      default: return null;
    }
  };
  return (
    <div style={{
      background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius,
      overflow: "hidden", marginTop: 12, boxShadow: T.shadow,
    }}>
      <div style={{
        padding: "12px 16px", background: `linear-gradient(135deg, ${T.bgAlt}, ${T.sidebar})`,
        borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <span style={{ color: T.text, fontFamily: T.serif, fontSize: 15, fontWeight: 600, letterSpacing: "-0.2px" }}>📊 {title}</span>
        <button onClick={() => setCollapsed(c => !c)} style={{
          background: T.surface, border: `1px solid ${T.border}`, color: T.textMid,
          borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 11, fontFamily: T.mono,
        }}>{collapsed ? "expand" : "collapse"}</button>
      </div>
      {!collapsed && (
        <>
          <div style={{ display: "flex", gap: 4, padding: "10px 14px", background: T.bg, borderBottom: `1px solid ${T.border}`, flexWrap: "wrap" }}>
            {CHART_TYPES.map(ct => (
              <button key={ct.id} onClick={() => setActiveType(ct.id)} style={{
                padding: "5px 12px", borderRadius: 20,
                border: `1.5px solid ${activeType === ct.id ? T.accent : T.border}`,
                background: activeType === ct.id ? T.accent : T.surface,
                color: activeType === ct.id ? "#fff" : T.textMid,
                fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: T.sans,
              }}><span style={{ marginRight: 4 }}>{ct.glyph}</span>{ct.label}</button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 10, padding: "10px 14px", background: T.bg, borderBottom: `1px solid ${T.border}`, flexWrap: "wrap" }}>
            {[["X-Axis / Label", xKey, setXKey], ["Y-Axis / Value", yKey, setYKey]].map(([lbl, val, set]) => (
              <div key={lbl} style={{ flex: "1 1 160px", minWidth: 140 }}>
                <label style={axLbl}>{lbl}</label>
                <select value={val} onChange={e => set(e.target.value)} style={axSel}>
                  {columns.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            ))}
            <div style={{ flex: "1 1 160px", minWidth: 140 }}>
              <label style={axLbl}>Title</label>
              <input value={title} onChange={e => setTitle(e.target.value)} style={axSel}/>
            </div>
          </div>
          <div style={{ padding: "14px 12px 8px" }}>
            {data.length > 0 ? render() : (
              <div style={{ textAlign: "center", color: T.textDim, padding: 40, fontSize: 13, fontFamily: T.serif, fontStyle: "italic" }}>
                No chartable data for this response
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
const axLbl = { fontSize: 10, color: T.textDim, fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", fontFamily: T.mono };
const axSel = { display: "block", width: "100%", marginTop: 4, padding: "6px 8px", border: `1px solid ${T.border}`, borderRadius: T.radiusSm, fontSize: 12, background: T.surface, color: T.text, outline: "none", fontFamily: T.sans };


function parseKoineResponse(raw) {
  if (!raw) return { data: null, sql: null, explanation: "" };
  let text = String(raw);
  let data = null;
  const dataMatch = text.match(/\{\s*"rows"\s*:\s*\[[\s\S]*?\]\s*,\s*"columns"\s*:\s*\[[^\]]*\]\s*\}/);
  if (dataMatch) {
    try {
      const parsed = JSON.parse(dataMatch[0]);
      if (Array.isArray(parsed.rows) && Array.isArray(parsed.columns)) {
        data = parsed; text = text.replace(dataMatch[0], "").trim();
      }
    } catch {}
  }
  let sql = null;
  const sqlMatches = [...text.matchAll(/```(?:sql)?\s*([\s\S]*?)```/g)];
  if (sqlMatches.length > 0) {
    sql = sqlMatches[sqlMatches.length - 1][1].replace(/^sql\s*\n/, "").trim();
    text = text.replace(/```(?:sql)?\s*[\s\S]*?```/g, "").trim();
  }
  text = text.replace(/--SQL Query:\s*/g, "").replace(/ClickHouse SQL Query\s*/g, "").replace(/--Question:[^\n]*/g, "").replace(/Wait\s*[—-]\s*let me correct[^\n]*/gi, "").replace(/\n{3,}/g, "\n\n").trim();
  return { data, sql, explanation: text };
}
function recommendKoineChart(cols, rows) {
  if (!cols || cols.length < 2 || !rows || rows.length === 0) return null;
  if (rows.length === 1) return null;
  const numericAt = cols.map((_, i) => rows.every(r => r[i] === null || r[i] === undefined || !isNaN(Number(r[i]))));
  const xIdx = numericAt.findIndex(n => !n);
  const yIdx = numericAt.findIndex(n => n);
  if (xIdx < 0 || yIdx < 0 || xIdx === yIdx) return null;
  const xKey = cols[xIdx], yKey = cols[yIdx];
  const looksLikeDate = /date|time|month|year|quarter|period|day|week/i.test(xKey);
  const suggestedType = looksLikeDate ? "line" : rows.length > 12 ? "line" : "bar";
  const chartData = rows.slice(0, 30).map(r => ({ [xKey]: String(r[xIdx] ?? ""), [yKey]: Number(r[yIdx]) || 0 }));
  return { title: `${yKey} by ${xKey}`, suggestedType, xKey, yKey, columns: cols, data: chartData };
}
function KoineScalar({ value, label }) {
  const fmtScalar = (v) => {
    const n = Number(v);
    if (!isFinite(n)) return String(v);
    if (Math.abs(n) >= 1_000_000_000) return (n / 1_000_000_000).toFixed(2) + "B";
    if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M";
    if (Math.abs(n) >= 1_000) return n.toLocaleString();
    return String(n);
  };
  return (
    <div style={{
      padding: "22px 24px", background: `linear-gradient(135deg, ${T.surface}, ${T.accentSoft}22)`,
      border: `1px solid ${T.border}`, borderLeft: `4px solid ${T.accent}`,
      borderRadius: T.radius, boxShadow: T.shadow, margin: "10px 0",
    }}>
      <div style={{ fontFamily: T.mono, fontSize: 11, color: T.textDim, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 8 }}>{label}</div>
      <div style={{ fontFamily: T.serif, fontSize: 36, fontWeight: 600, color: T.text, lineHeight: 1.1, letterSpacing: "-1px" }}>{fmtScalar(value)}</div>
    </div>
  );
}
function downloadDataAsCsv(cols, rows, filename = "data.csv") {
  const esc = (v) => { const s = v === null || v === undefined ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const csv = [cols.map(esc).join(","), ...rows.map(r => r.map(esc).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function KoineAnswer({ content }) {
  const { data, sql, explanation } = useMemo(() => parseKoineResponse(content), [content]);
  const [showSql, setShowSql] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const chartData = useMemo(() => data ? recommendKoineChart(data.columns, data.rows) : null, [data]);
  const isScalar = data && data.rows.length === 1 && data.columns.length === 1;
  if (!data && !sql && !explanation) return <div style={{ fontFamily: T.sans, color: T.textMid, fontSize: 13.5 }}>{content}</div>;
  return (
    <div>
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 10,
        padding: "3px 10px", background: T.accentSoft, color: T.accent,
        borderRadius: 20, border: `1px solid ${T.border}`,
        fontSize: 10.5, fontFamily: T.mono, fontWeight: 600, letterSpacing: "0.3px",
      }}>
        <Icon name="zap" size={10}/> via Koine
      </div>
      {isScalar && <KoineScalar value={data.rows[0][0]} label={data.columns[0]}/>}
      {data && !isScalar && (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6, flexWrap: "wrap", gap: 6 }}>
            <span style={{ fontFamily: T.mono, fontSize: 11, color: T.textDim, letterSpacing: "0.4px" }}>
              {data.rows.length} row{data.rows.length === 1 ? "" : "s"} · {data.columns.length} column{data.columns.length === 1 ? "" : "s"}
            </span>
            <button onClick={() => downloadDataAsCsv(data.columns, data.rows, "koine_data.csv")} style={{
              padding: "3px 10px", borderRadius: 6, background: T.surface, border: `1px solid ${T.border}`,
              color: T.textMid, cursor: "pointer", fontSize: 11, fontFamily: T.mono, fontWeight: 600,
              display: "inline-flex", alignItems: "center", gap: 5,
            }}>↓ CSV</button>
          </div>
          <DataTable columns={data.columns} rows={data.rows}/>
        </>
      )}
      {chartData && <InlineChart chartData={chartData}/>}
      {(sql || explanation) && (
        <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap" }}>
          {sql && <button onClick={() => setShowSql(s => !s)} style={koineToggle(showSql)}><span style={{ fontSize: 12 }}>⌘</span>{showSql ? "hide SQL" : "show SQL"}</button>}
          {explanation && <button onClick={() => setShowNotes(s => !s)} style={koineToggle(showNotes)}><span style={{ fontSize: 12 }}>✎</span>{showNotes ? "hide notes" : "show notes"}</button>}
        </div>
      )}
      {sql && showSql && (
        <div style={{ marginTop: 8, borderRadius: T.radiusSm, overflow: "hidden", border: `1px solid ${T.border}`, background: T.bgAlt }}>
          <pre style={{ background: T.code, color: T.codeText, borderRadius: T.radiusSm, padding: "14px 16px", fontSize: 12, overflowX: "auto", fontFamily: T.mono, margin: 0, lineHeight: 1.55 }}>
            <code>{sql}</code>
          </pre>
        </div>
      )}
      {explanation && showNotes && (
        <div style={{
          marginTop: 8, padding: "12px 14px", background: T.bgAlt,
          border: `1px solid ${T.border}`, borderRadius: T.radiusSm,
          fontFamily: T.serif, fontSize: 13, color: T.textMid,
          lineHeight: 1.6, whiteSpace: "pre-wrap", fontStyle: "italic",
        }}>{explanation}</div>
      )}
    </div>
  );
}
const koineToggle = (active) => ({
  padding: "4px 11px", borderRadius: 20,
  border: `1px solid ${active ? T.accent : T.border}`,
  background: active ? T.accentSoft : T.surface,
  color: active ? T.accent : T.textMid,
  fontSize: 11, fontFamily: T.mono, fontWeight: 600,
  cursor: "pointer", letterSpacing: "0.3px",
  display: "inline-flex", alignItems: "center", gap: 5, transition: "all 0.15s",
});


function MessageRow({ m, token, threadId, prevUserQ }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(stripChartBlock(m.content || "")).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    });
  };
  const isUser = m.role === "user";
  const statCards = useMemo(() => {
    if (isUser || !m.content || m.computing || m.viaKoine) return null;
    return extractStatCards(stripChartBlock(m.content));
  }, [isUser, m.content, m.computing, m.viaKoine]);
  const widgetSpec = useMemo(() => {
    if (isUser || !m.content || m.computing || m.viaKoine) return null;
    return extractWidgetSpec(m.content);
  }, [isUser, m.content, m.computing, m.viaKoine]);
  return (
    <div style={{ marginBottom: 28, animation: "fadeUp 0.3s" }}>
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start", flexDirection: isUser ? "row-reverse" : "row" }}>
        <div style={{
          width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
          background: isUser
            ? `linear-gradient(135deg, ${T.accentDark}, ${T.accentInk})`
            : `linear-gradient(135deg, ${T.accent}, ${T.accentDark} 70%, ${T.accentInk})`,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: T.shadow,
          border: `1px solid ${isUser ? T.accentInk : T.accentDark}`,
        }}>
          <Icon name={isUser ? "user" : "sparkles"} size={14} color="#fff"/>
        </div>
        <div style={{
          maxWidth: isUser ? "78%" : "100%",
          flex: isUser ? "none" : "1 1 auto", minWidth: 0,
          padding: isUser ? "11px 16px" : "4px 0",
          borderRadius: isUser ? "18px 4px 18px 18px" : 0,
          background: isUser ? T.userBubble : "transparent",
          color: T.text, fontSize: 14.5, lineHeight: 1.68,
          boxShadow: isUser ? T.shadow : "none",
          border: isUser ? `1px solid ${T.border}` : "none",
        }}>
          {isUser ? (
            <div style={{ whiteSpace: "pre-wrap", fontFamily: T.sans, color: T.text, fontWeight: 500 }}>
              {m.content}
            </div>
          ) : m.computing && !m.content ? (
            <ThinkingIndicator/>
          ) : m.content ? (
            m.viaKoine ? (
              <KoineAnswer content={m.content}/>
            ) : (
              <>
                {/* {(m.pandasResult || m.pandasError) && (
                  <ComputationPanel code={m.pandasCode} result={m.pandasResult} error={m.pandasError}/>
                )} */}
                <SourcesPanel sources={m.sources} bingSources={m.bingSources}/>
                {statCards && <StatCards cards={statCards}/>}
                <RenderMessage text={stripChartBlock(m.content)}/>
                {widgetSpec && <AdaptiveWidget spec={widgetSpec}/>}
                {m.chartData && <InlineChart chartData={m.chartData}/>}
              </>
            )
          ) : <PulseDots/>}
        </div>
      </div>

      {!isUser && m.content && !m.computing && (
        <div style={{
          marginLeft: 48, marginTop: 8, display: "flex",
          gap: 4, alignItems: "center", flexWrap: "wrap",
        }}>
          <button onClick={copy} title="Copy" style={actionBtn}
            onMouseEnter={e => e.currentTarget.style.background = T.bgAlt}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            <Icon name={copied ? "check" : "copy"} size={11}/>
            {copied ? "Copied" : "Copy"}
          </button>
          <div style={{ width: 1, height: 14, background: T.border, margin: "0 4px" }}/>
          <FeedbackWidget
            threadId={threadId} messageId={m.id || "unknown"}
            userQuestion={m.userQuestion || prevUserQ}
            assistantResponse={stripChartBlock(m.content)}
            token={token}
          />
          {m.ts && (
            <span style={{
              fontSize: 11, color: T.textDim, padding: "4px 8px",
              marginLeft: "auto", fontFamily: T.mono, letterSpacing: "0.3px",
            }}>{fmt(m.ts)}</span>
          )}
        </div>
      )}
    </div>
  );
}

const actionBtn = {
  padding: "4px 9px", borderRadius: 6, border: "none",
  background: "transparent", cursor: "pointer", color: T.textDim,
  fontSize: 11.5, display: "flex", alignItems: "center", gap: 5,
  fontFamily: T.sans, fontWeight: 500, transition: "background 0.15s",
};

const iconBtn = {
  width: 36, height: 36, borderRadius: T.radiusSm,
  background: T.bg, border: `1px solid ${T.border}`,
  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
  padding: 0, transition: "all 0.15s", flexShrink: 0,
};
const metaTag = {
  fontSize: 10, color: T.textDim, background: T.bg,
  padding: "2px 8px", borderRadius: 10, border: `1px solid ${T.border}`,
  fontFamily: T.mono, letterSpacing: "0.3px",
};

/* ════════════════════════════════════════════════════════════════════════
   MAIN APP
   ════════════════════════════════════════════════════════════════════════ */
export default function DataRAGChat() {
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState(null);
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [activeThread, setActiveThread] = useState(null);
  const [loadingThread, setLoadingThread] = useState(null);
  const [token] = useState(getStoredToken());
  const [user] = useState(getStoredUser());

  // Per-turn tool config
  const [tool, setTool] = useState("auto");
  const [bing, setBing] = useState(false);
  const [koine, setKoine] = useState(false);
  const koineThreadRef = useRef(null);

  // Speech
  const [recording, setRecording] = useState(false);
  const recognitionRef = useRef(null);

  // Viz drawer (KG/mindmap)
  const [vizData, setVizData] = useState(null);
  const [vizOpen, setVizOpen] = useState(false);
  const [vizLoading, setVizLoading] = useState(false);
  const [userPhoto, setUserPhoto] = useState(() => localStorage.getItem('konet_user_photo') || null);

  // Clarifier
  const [clarifier, setClarifier] = useState(null);

  // Responsive
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 820 : false
  );
  const [sidebarOpen, setSidebarOpen] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 820 : true
  );

  // ─────────────────────────────────────────────────────────────
  // DASHBOARD INTEGRATION STATE
  // ─────────────────────────────────────────────────────────────
  const [view, setView] = useState("chat");  // "chat" | "dashboard"
  const [dashData, setDashData] = useState(null);
  const [dashFileName, setDashFileName] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);
  const [dashError, setDashError] = useState(null);
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);  // RAG chat as side drawer

  const fileRef = useRef(null);
  const dashFileRef = useRef(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!token) return;
    const cached = localStorage.getItem('konet_user_photo');
    if (cached) { setUserPhoto(cached); return; }
    fetch(`${process.env.REACT_APP_BACKEND}/api/auth/photo`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (data.photo) {
          localStorage.setItem('konet_user_photo', data.photo);
          setUserPhoto(data.photo);
        }
      })
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    const handle = () => {
      const mobile = window.innerWidth < 820;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(true);
      else setSidebarOpen(false);
    };
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);

  useEffect(() => { document.title = "KOgnos AI"; }, []);
  useEffect(() => {
    if (!token) {
      localStorage.setItem("login_redirect_to", location.pathname + location.search);
      navigate("/", { replace: true });
    }
  }, [token, navigate, location]);

  const refreshThreads = useCallback(() => {
    setThreadsLoading(true);
    authedFetch(`${API_BASE}/threads`)
      .then(r => r.json())
      .then(d => setThreads(d?.threads || []))
      .catch(() => {})
      .finally(() => setThreadsLoading(false));
  }, []);
  useEffect(() => { if (token) refreshThreads(); }, [token, refreshThreads]);
const chatScrollRef = useRef(null);
const userScrolledRef = useRef(false);

useEffect(() => {
  const el = chatScrollRef.current;
  if (!el) return;
  const onScroll = () => {
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    userScrolledRef.current = distFromBottom > 120;
  };
  el.addEventListener("scroll", onScroll, { passive: true });
  return () => el.removeEventListener("scroll", onScroll);
}, []);

useEffect(() => {
  if (!userScrolledRef.current) {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }
}, [messages]);

  const loadThread = useCallback(async (sessionId) => {
    setLoadingThread(sessionId);
    setDashData(null);
    try {
      const res = await authedFetch(`${API_BASE}/threads/${sessionId}`);
      if (!res.ok) return;
      const data = await res.json();
      setActiveThread(sessionId);
      const names = data?.sheets_meta?.map(s => s.name) || [];
      const isKoineThread = data.tool === "koine";
      setSession({
        session_id:  data.session_id,
        file_name:   data.file_name,
        file_kind:   data.file_kind || "none",
        sheet_names: names,
        tool:        data.tool || "",
      });
      setMessages((data.history || []).map((m, idx) => ({
        id: `h${idx}`,
        role: m.role,
        content: m.content,
        ts: new Date().toISOString(),
        viaKoine: m.role === "assistant" && (m.viaKoine === true || isKoineThread),
        chartData: (m.role === "assistant" && !isKoineThread && !m.viaKoine)
          ? extractChartData(m.content) : null,
      })));
      setClarifier(null);
      setVizData(null);
      koineThreadRef.current = data.koine_thread_id || null;
      if (isKoineThread) setKoine(true);
      // Switch back to chat view when loading a past thread
      setView("chat");
      setChatDrawerOpen(false);
      if (isMobile) setSidebarOpen(false);
    } catch {}
    setLoadingThread(null);
  }, [isMobile]);

  const newChat = () => {
    setSession(null);
    setMessages([]);
    setActiveThread(null);
    setInput("");
    setError(null);
    setVizData(null);
    setClarifier(null);
    koineThreadRef.current = null;
    setView("chat");
    setChatDrawerOpen(false);
    if (isMobile) setSidebarOpen(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // ─────────────────────────────────────────────────────────────
  // DASHBOARD HANDLERS
  // ─────────────────────────────────────────────────────────────

  /** Parse a file for the dashboard (client-side via SheetJS).
   *  Returns the parsed dashboard data shape. */
  const parseFileForDashboard = useCallback(async (file, sessionId) => {
    setDashLoading(true);
    setDashError(null);
    try {
      const parsed = await parseSpreadsheetFile(file,sessionId);
      setDashData(parsed);
      setDashFileName(file.name);
      return parsed;
    } catch (e) {
      setDashError(e.message || "Failed to parse file");
      return null;
    } finally {
      setDashLoading(false);
    }
  }, []);
  const handleUpload = useCallback(async (file) => {
    if (!file) return;
    const ext = getFileExt(file.name);
    if (!SUPPORTED_EXT.includes(ext)) {
      setError(`Unsupported file. Allowed: ${SUPPORTED_EXT.join(", ")}`);
      return;
    }
    setUploading(true); setError(null);

    // Upload to RAG backend (so the chat works on the file)
    const form = new FormData();
    form.append("file", file);
    if (session?.session_id) form.append("session_id", session.session_id);
    try {
      const res = await authedFetch(`${API_BASE}/upload`, { method: "POST", body: form });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || "Upload failed");
      }
      const data = await res.json();
      setSession({
        session_id:  data.session_id,
        file_name:   data.file_name,
        file_kind:   data.file_kind,
        sheet_names: data.sheet_names || [],
      });
      setActiveThread(data.session_id);
      const sheetsInfo = data.sheet_names?.length > 1
        ? `\n\nDetected **${data.sheet_names.length} sheets**: ${data.sheet_names.map(n => `\`${n}\``).join(", ")}`
        : "";
      const kindLabel = data.file_kind === "tabular" ? "spreadsheet"
                      : data.file_kind === "text" ? "document" : "file";
      setMessages(prev => [...prev, {
        id: `up${Date.now()}`,
        role: "assistant",
        content: `**${data.file_name}** is loaded as a ${kindLabel}.${sheetsInfo}\n\nAsk me anything about it — or open the **Dashboard** from the sidebar for a visual breakdown.`,
        ts: new Date().toISOString(),
      }]);
      refreshThreads();

      // If it's a spreadsheet, also parse it client-side for the dashboard
      // and auto-switch to the dashboard view (great first impression).
      if (isSpreadsheet(file.name)) {
        const parsed = await parseFileForDashboard(file, data?.session_id);
        if (parsed) {
          // setView("dashboard");
          // Mobile: collapse sidebar so dashboard takes full width
          if (isMobile) setSidebarOpen(false);
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  }, [session, refreshThreads, parseFileForDashboard, isMobile]);

  /** Load the bundled /preview.xlsx sample */
const loadSampleDashboard = useCallback(async () => {
  setDashLoading(true);
  setDashError(null);
  try {
    const resp = await fetch(PREVIEW_FILE_URL);
    if (!resp.ok) throw new Error(`Sample file not found at ${PREVIEW_FILE_URL}`);
    const blob = await resp.blob();
    const file = new File([blob], "preview.xlsx", { type: blob.type });
    // handleUpload parses for the dashboard AND uploads to backend (creates session)
    await handleUpload(file);
    return file;
  } catch (e) {
    setDashError(e.message || "Failed to load sample file");
    return null;
  } finally {
    setDashLoading(false);
  }
}, [handleUpload]);
const loadSampleDashboard3 = useCallback(async () => {
  setDashLoading(true);
  setDashError(null);
  try {
    const resp = await fetch(PREVIEW_FILE_URL_3);
    if (!resp.ok) throw new Error(`Sample file not found at ${PREVIEW_FILE_URL_3}`);
    const blob = await resp.blob();
    const file = new File([blob], "swa_volume_growth.xlsx", { type: blob.type });
    await handleUpload(file);
    return file;
  } catch (e) {
    setDashError(e.message || "Failed to load sample file");
    return null;
  } finally {
    setDashLoading(false);
  }
}, [handleUpload]);
const loadSampleDashboard4 = useCallback(async () => {
  setDashLoading(true); setDashError(null);
  try {
    const resp = await fetch(PREVIEW_FILE_URL_4);
    if (!resp.ok) throw new Error(`Sample file not found at ${PREVIEW_FILE_URL_4}`);
    const blob = await resp.blob();
    await handleUpload(new File([blob], "ddr_outlets.xlsx", { type: blob.type }));
  } catch (e) {
    setDashError(e.message || "Failed to load sample file");
  } finally { setDashLoading(false); }
}, [handleUpload]);
const loadSampleDashboard2 = useCallback(async () => {
  setDashLoading(true);
  setDashError(null);
  try {
    const resp = await fetch(PREVIEW_FILE_URL_2);
    if (!resp.ok) throw new Error(`Sample file not found at ${PREVIEW_FILE_URL_2}`);
    const blob = await resp.blob();
    const file = new File([blob], "fbo_volume_growth.xlsx", { type: blob.type });
    await handleUpload(file);
    return file;
  } catch (e) {
    setDashError(e.message || "Failed to load sample file");
    return null;
  } finally {
    setDashLoading(false);
  }
}, [handleUpload]);

  /** Open the dashboard view. If we have a spreadsheet session already
   *  parsed for the dashboard, just switch. Otherwise show the empty state. */
  const openDashboard = useCallback(() => {
    setSession(null);
    setMessages([]);
    setActiveThread(null);
    setInput("");
    setError(null);
    setVizData(null);
    setClarifier(null);
    koineThreadRef.current = null;
    setView("dashboard");
    setDashData(null);
    setDashFileName(null);
    setDashError(null);
    setChatDrawerOpen(false);
    if (isMobile) setSidebarOpen(false);
  }, [isMobile]);

  /** Reset dashboard data (NEW FILE button inside dashboard) */
  const resetDashboard = useCallback(() => {
    setDashData(null);
    setSession(null);
    setDashFileName(null);
    setDashError(null);
  }, []);

  /** When a spreadsheet is uploaded via the chat composer, also parse
   *  it for the dashboard and auto-switch to dashboard view. */

  const onFileChange = e => handleUpload(e.target.files[0]);
  const onDashFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Parse for dashboard
    await parseFileForDashboard(file);
    // Also upload to RAG so the side-drawer chat works on it
    handleUpload(file);
  };
  const onDrop = e => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files[0]); };

  const attemptSend = (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || streaming) return;
    const ambig = detectAmbiguity(text, !!session?.file_name);
    if (ambig) { setClarifier(ambig); return; }
    sendMessage(text);
  };

  const sendMessage = async (text) => {
    if (!text || streaming) return;
    setClarifier(null);

    setMessages(prev => [...prev, {
      id: `u${Date.now()}`, role: "user", content: text, ts: new Date().toISOString(),
    }]);
    setInput("");
    setStreaming(true);
    setError(null);

    const pid = `a${Date.now()}`;
    setMessages(prev => [...prev, {
      role: "assistant", content: "", ts: new Date().toISOString(),
      id: pid, computing: true,
      userQuestion: text,
      pandasCode: null, pandasResult: null, pandasError: null,
      sources: [], bingSources: [],
    }]);

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    if (koine) {
      try {
        const res = await authedFetch(KOINE_BASE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            question: text,
            session_id: session?.session_id || null,
            thread_id: koineThreadRef.current,
            chat_history: messages
              .filter(m => !m.computing)
              .slice(-6)
              .map(m => ({ role: m.role, content: stripChartBlock(m.content || "").slice(0, 2000) })),
          }),
          signal: ctrl.signal,
        });
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new Error(e.detail || `Koine request failed (${res.status})`);
        }
        const data = await res.json();
        if (data.thread_id) koineThreadRef.current = data.thread_id;
        if (data.session_id && !session?.session_id) {
          setSession(s => ({ ...(s || {}), session_id: data.session_id, file_kind: "none" }));
          setActiveThread(data.session_id);
        }
        const answer = (data.answer || "").trim() || "(Koine returned no answer)";
        setMessages(prev => prev.map(m => m.id === pid
          ? { ...m, computing: false, content: answer, userQuestion: text, viaKoine: true }
          : m));
        refreshThreads();
      } catch (e) {
        if (e.name !== "AbortError") {
          setError(`Koine: ${e.message}`);
          setMessages(prev => prev.filter(m => m.id !== pid));
        }
      } finally {
        setStreaming(false);
        abortRef.current = null;
        inputRef.current?.focus();
      }
      return;
    }

    try {
      const res = await authedFetch(`${API_BASE}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: session?.session_id || null,
          message: text, tool, bing,
        }),
        signal: ctrl.signal,
      });
      if (!res.ok) throw new Error("Chat request failed");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          let payload;
          try { payload = JSON.parse(line.slice(6)); } catch { continue; }
          if (payload.error) throw new Error(payload.error);
          if (payload.sessionId && !session?.session_id) {
            setSession(s => ({ ...(s || {}), session_id: payload.sessionId, file_kind: "none" }));
            setActiveThread(payload.sessionId);
          }
          if (payload.pandasResult !== undefined) {
            setMessages(prev => prev.map(m => m.id === pid
              ? { ...m, computing: false, pandasCode: payload.pandasCode, pandasResult: payload.pandasResult }
              : m));
          }
          if (payload.pandasError !== undefined) {
            setMessages(prev => prev.map(m => m.id === pid
              ? { ...m, computing: false, pandasError: payload.pandasError } : m));
          }
          if (payload.sources) {
            setMessages(prev => prev.map(m => m.id === pid
              ? { ...m, computing: false, sources: payload.sources } : m));
          }
          if (payload.bingSources) {
            setMessages(prev => prev.map(m => m.id === pid
              ? { ...m, computing: false, bingSources: payload.bingSources } : m));
          }
          if (payload.knowledgeGraph) {
            setVizData(v => ({ ...(v || {}), kg: payload.knowledgeGraph }));
          }
          if (payload.mindmap) {
            setVizData(v => ({ ...(v || {}), mindmap: payload.mindmap }));
          }
          if (payload.delta) {
            setMessages(prev => prev.map(m => m.id === pid
              ? { ...m, computing: false, content: m.content + payload.delta } : m));
          }
          if (payload.chartData) {
            setMessages(prev => prev.map(m => m.id === pid
              ? { ...m, chartData: payload.chartData } : m));
          }
        }
      }
      setMessages(prev => prev.map(m => {
        if (m.id !== pid) return m;
        const cd = m.chartData || extractChartData(m.content);
        return { ...m, chartData: cd };
      }));
      refreshThreads();
    } catch (e) {
      if (e.name !== "AbortError") {
        setError(e.message);
        setMessages(prev => prev.filter(m => m.id !== pid));
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
      inputRef.current?.focus();
    }
  };

  const stopStreaming = () => { abortRef.current?.abort(); };

  const onKeyDown = e => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); attemptSend(); }
  };

  const toggleMic = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setError("Speech recognition not supported in this browser (try Chrome)."); return; }
    if (recording) { recognitionRef.current?.stop(); setRecording(false); return; }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-US";
    rec.onresult = (e) => {
      let interim = "", final = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += t;
        else interim += t;
      }
      setInput(prev => (final ? prev + final : prev) + (interim ? ` ${interim}` : ""));
    };
    rec.onerror = (e) => { setError(`Mic error: ${e.error}`); setRecording(false); };
    rec.onend = () => setRecording(false);
    rec.start();
    recognitionRef.current = rec;
    setRecording(true);
  };

  const deleteThread = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await authedFetch(`${API_BASE}/threads/${sid}`, { method: "DELETE" });
      if (activeThread === sid) newChat();
      refreshThreads();
    } catch {}
  };

  const openViz = async () => {
    if (!session?.session_id || session.file_kind !== "text") return;
    setVizOpen(true);
    if (!vizData?.kg || !vizData?.mindmap) {
      setVizLoading(true);
      try {
        const res = await authedFetch(`${API_BASE}/threads/${session.session_id}/knowledge-graph`);
        if (res.ok) {
          const data = await res.json();
          setVizData({ kg: data.knowledgeGraph, mindmap: data.mindmap });
        }
      } catch {}
      setVizLoading(false);
    }
  };

  if (!token) return <div>Loading...</div>;

  // Whether a top-bar Dashboard toggle should appear (file is spreadsheet)
  const showDashboardToggle = view === "chat" && isSpreadsheet(session?.file_name);

  return (
    <div style={{
      display: "flex", height: "100vh", width: "100%",
      fontFamily: T.sans, background: T.bg, color: T.text, overflow: "hidden",
      position: "relative",
    }}>
      {isMobile && sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} style={{
          position: "fixed", inset: 0, background: "rgba(30,25,20,0.4)",
          zIndex: 40, animation: "fadeIn 0.15s",
        }}/>
      )}

      {/* ═════════════ SIDEBAR ═════════════ */}
      <aside style={{
        width: sidebarOpen ? 280 : 0,
        minWidth: sidebarOpen ? 280 : 0,
        background: T.sidebar,
        borderRight: `1px solid ${T.border}`,
        display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden",
        transition: "width 0.25s ease, min-width 0.25s ease",
        position: isMobile ? "fixed" : "relative",
        zIndex: 45, left: 0, top: 0,
      }}>
        <div style={{
          padding: "18px 18px 14px", display: "flex", alignItems: "center",
          gap: 12, flexShrink: 0,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 11,
            background: `linear-gradient(135deg, ${T.accent}, ${T.accentDark} 70%, ${T.accentInk})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: T.shadow,
          }}>
            <Icon name="sparkles" size={18} color="#fff"/>
          </div>
          <div>
            <div style={{
              fontFamily: T.serif, fontSize: 17, fontWeight: 600,
              color: T.text, letterSpacing: "-0.4px", lineHeight: 1,
            }}>KOgnos AI</div>
            <div style={{ fontSize: 10.5, color: T.textDim, marginTop: 3 }}>
              Research · Analysis · Chat
            </div>
          </div>
        </div>

        <div style={{ padding: "0 14px 6px", flexShrink: 0 }}>
          <button onClick={newChat} style={sidebarBtn(view === "chat" && !activeThread)}>
            <Icon name="plus" size={14}/>
            New conversation
          </button>
        </div>

        {/* ─── DASHBOARD BUTTON ─── */}
        <div style={{ padding: "0 14px 10px", flexShrink: 0 }}>
          <button onClick={openDashboard} style={{
            width: "100%", padding: "11px 14px", borderRadius: T.radiusSm,
            background: view === "dashboard"
              ? `linear-gradient(135deg, ${T.accent}, ${T.accentDark})`
              : T.surface,
            color: view === "dashboard" ? "#fff" : T.text,
            border: `1px solid ${view === "dashboard" ? T.accent : T.border}`,
            cursor: "pointer", fontSize: 13.5, fontWeight: 600,
            display: "flex", alignItems: "center", gap: 10,
            transition: "all 0.15s", fontFamily: T.sans,
            boxShadow: view === "dashboard" ? T.shadowMd : T.shadow,
          }}>
            <Icon name="grid" size={14}/>
            Create New Dashboard
            {/* {dashData && (
              <span style={{
                marginLeft: "auto", fontSize: 9, padding: "2px 7px", borderRadius: 8,
                background: view === "dashboard" ? "rgba(255,255,255,0.25)" : T.accentSoft,
                color: view === "dashboard" ? "#fff" : T.accent,
                fontFamily: T.mono, letterSpacing: "0.5px", fontWeight: 700,
              }}>{dashData.isHCCB ? "HCCB+" : "AUTO"}</span>
            )} */}
          </button>
        </div>

        {/* Threads list */}
        <div style={{
          flex: "1 1 0", minHeight: 0, overflowY: "auto", padding: "4px 10px 10px",
          scrollbarWidth: "thin",
        }}>
          {threadsLoading && threads.length === 0 ? (
            <div style={{
              padding: "20px 12px", display: "flex", alignItems: "center",
              gap: 8, color: T.textDim, fontSize: 12,
            }}>
              <Spinner size={12} color={T.textDim}/>
              <span style={{ fontStyle: "italic", fontFamily: T.serif }}>loading history…</span>
            </div>
          ) : threads.length > 0 ? (
            <>
              <div style={{
                fontSize: 10, fontWeight: 700, color: T.textDim,
                padding: "12px 10px 6px", letterSpacing: "1.2px",
                textTransform: "uppercase", fontFamily: T.mono,
              }}>Recent</div>
              {threads.map(t => {
                const isLoadingT = loadingThread === t.sessionId;
                const isActive = activeThread === t.sessionId && view === "chat";
                return (
                  <div key={t.sessionId} onClick={() => loadThread(t.sessionId)} className="thread-row"
                    style={{
                      padding: "9px 11px", borderRadius: T.radiusSm, cursor: "pointer",
                      marginBottom: 2, display: "flex", alignItems: "center", gap: 9,
                      background: isActive ? T.surface : "transparent",
                      color: isActive ? T.accent : T.text,
                      fontSize: 13, fontWeight: isActive ? 600 : 500,
                      transition: "all 0.1s", position: "relative", fontFamily: T.sans,
                      boxShadow: isActive ? T.shadow : "none",
                    }}>
                    {isLoadingT ? <Spinner size={12} color={T.accent}/>
                      : <Icon name="chat" size={13} color={isActive ? T.accent : T.textDim}/>}
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                      {t.title || t.fileName || "New chat"}
                    </span>
                    <button onClick={e => deleteThread(t.sessionId, e)} className="thread-del" style={{
                      background: "none", border: "none", cursor: "pointer",
                      padding: 3, color: T.textDim, display: "none", borderRadius: 4,
                    }}>
                      <Icon name="trash" size={12}/>
                    </button>
                  </div>
                );
              })}
            </>
          ) : (
            <div style={{
              padding: "20px 12px", textAlign: "center",
              color: T.textDim, fontSize: 12.5, fontStyle: "italic", fontFamily: T.serif,
            }}>No conversations yet. Start one above.</div>
          )}
        </div>

        <div style={{ padding: "12px 14px 16px", borderTop: `1px solid ${T.border}`, flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            {userPhoto
              ? <img alt="" style={{
                  width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                  boxShadow: T.shadow,
                }} src={userPhoto}/>
              : <div style={{
                  width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                  background: `linear-gradient(135deg, ${T.accentDark}, ${T.accentInk})`,
                  color: "#fff", display: "flex",
                  alignItems: "center", justifyContent: "center",
                  fontSize: 13, fontWeight: 700, fontFamily: T.serif, boxShadow: T.shadow,
                }}>
                  {(user?.name || "U").charAt(0).toUpperCase()}
                </div>
            }
            <div style={{ overflow: "hidden", flex: 1 }}>
              <div style={{
                fontSize: 13, fontWeight: 600, color: T.text, overflow: "hidden",
                textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: T.sans,
              }}>{user?.name || "User"}</div>
              <div style={{
                fontSize: 10.5, color: T.textDim, overflow: "hidden",
                textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: T.mono,
              }}>{user?.email || ""}</div>
            </div>
          </div>
          <button onClick={() => { clearAuth(); window.location.reload(); }} style={{
            width: "100%", padding: "7px 10px", borderRadius: T.radiusSm,
            background: "transparent", color: T.textMid, border: `1px solid ${T.border}`,
            cursor: "pointer", fontSize: 12, fontWeight: 500,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            fontFamily: T.sans,
          }}>
            <Icon name="logout" size={12}/> Sign out
          </button>
        </div>
      </aside>

      {/* ═════════════ MAIN ═════════════ */}
      <main style={{
        flex: 1, display: "flex", flexDirection: "column",
        overflow: "hidden", position: "relative", minWidth: 0,
      }}>
        {view === "dashboard" ? (
          <DashboardView
            session={session}
            onLoadSample3={loadSampleDashboard3}
              onLoadSample4={loadSampleDashboard4} 
            data={dashData} fileName={dashFileName}
            loading={dashLoading} error={dashError}
            onLoadSample2={loadSampleDashboard2}
            onLoadSample={loadSampleDashboard}
            onUpload={() => dashFileRef.current?.click()}
            onAskAI={() => setChatDrawerOpen(true)}
            onBackToChat={() => setView("chat")}
            onReset={resetDashboard}
            onToggleSidebar={() => setSidebarOpen(o => !o)}
            isMobile={isMobile}
          />
        ) : (
          <ChatView
  chatScrollRef={chatScrollRef}
            session={session} messages={messages} input={input} setInput={setInput}
            uploading={uploading} streaming={streaming} dragOver={dragOver}
            setDragOver={setDragOver} onDrop={onDrop} error={error} setError={setError}
            clarifier={clarifier} setClarifier={setClarifier}
            attemptSend={attemptSend} sendMessage={sendMessage} stopStreaming={stopStreaming}
            onKeyDown={onKeyDown} fileRef={fileRef} onFileChange={onFileChange}
            inputRef={inputRef} bottomRef={bottomRef}
            tool={tool} setTool={setTool} bing={bing} setBing={setBing}
            koine={koine} setKoine={setKoine}
            recording={recording} toggleMic={toggleMic}
            sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}
            isMobile={isMobile} user={user} token={token}
            activeThread={activeThread}
            showDashboardToggle={showDashboardToggle}
            onOpenDashboard={async () => {
              // If we don't have parsed dashboard data yet but the session has a file,
              // re-parse from preview as a fallback (rare path — user uploaded but
              // parsing failed). Most of the time, dashData is already populated.
              if (!dashData) {
                // No client-side data; this happens only on a re-loaded session
                // where the original file is on the server. Show empty state.
              }
              setView("dashboard");
              setChatDrawerOpen(false);
            }}
            onOpenViz={openViz}
          />
        )}
      </main>

      {/* Dashboard file input (hidden) */}
      <input ref={dashFileRef} type="file" accept={SPREADSHEET_EXT.join(",")}
        style={{ display: "none" }} onChange={onDashFileChange}/>

      {/* RAG chat drawer (used when in dashboard view) */}
      {chatDrawerOpen && (
        <ChatDrawer
          onClose={() => setChatDrawerOpen(false)}
          session={session} messages={messages} input={input} setInput={setInput}
          streaming={streaming} error={error} setError={setError}
          clarifier={clarifier} setClarifier={setClarifier}
          attemptSend={attemptSend} sendMessage={sendMessage} stopStreaming={stopStreaming}
          onKeyDown={onKeyDown}
          tool={tool} setTool={setTool} bing={bing} setBing={setBing}
          koine={koine} setKoine={setKoine}
          token={token} user={user} activeThread={activeThread}
          dashFileName={dashFileName}
        />
      )}

      {vizOpen && (
        <VizDrawer kg={vizData?.kg} mindmap={vizData?.mindmap}
          onClose={() => setVizOpen(false)} loading={vizLoading}/>
      )}

      <GlobalStyles/>
    </div>
  );
}

const sidebarBtn = (active) => ({
  width: "100%", padding: "11px 14px", borderRadius: T.radiusSm,
  background: T.surface, color: active ? T.accent : T.text,
  border: `1px solid ${active ? T.accent : T.border}`,
  cursor: "pointer", fontSize: 13.5, fontWeight: 600,
  display: "flex", alignItems: "center", gap: 10,
  transition: "all 0.15s", fontFamily: T.sans,
  boxShadow: T.shadow,
});

function GlobalStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,400;1,9..144,500;1,9..144,600&family=Geist:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: ${T.sans}; background: ${T.bg}; }
      ::-webkit-scrollbar { width: 8px; height: 8px; }
      ::-webkit-scrollbar-thumb { background: ${T.borderStrong}; border-radius: 4px; }
      ::-webkit-scrollbar-thumb:hover { background: ${T.textDim}; }
      ::-webkit-scrollbar-track { background: transparent; }
      .thread-row:hover { background: ${T.surface} !important; }
      .thread-row:hover .thread-del { display: inline-flex !important; }
      .suggest-card:hover {
        border-color: ${T.accent} !important;
        background: ${T.accentSoft} !important;
        transform: translateY(-2px);
        box-shadow: ${T.shadowMd};
      }
      .clarifier-opt:hover {
        border-color: ${T.accent} !important;
        background: ${T.accentSoft} !important;
        transform: translateX(2px);
      }
      .tool-opt:hover { background: ${T.bg} !important; }
      .stat-card { transition: transform 0.15s, box-shadow 0.15s; }
      .stat-card:hover { transform: translateY(-2px); box-shadow: ${T.shadowMd}; }
      textarea, input, select, button { font-family: ${T.sans}; }
      button:focus-visible, textarea:focus-visible, select:focus-visible, input:focus-visible {
        outline: 2px solid ${T.accent}; outline-offset: 1px;
      }
      textarea:focus, input:focus { outline: none; }
      @keyframes pulse { 0%,100% { opacity:.3; transform:scale(.8); } 50% { opacity:1; transform:scale(1); } }
      @keyframes spin { to { transform: rotate(360deg); } }
      @keyframes orbSpin { to { transform: rotate(360deg); } }
      @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
      @keyframes fadeUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes fadeSlide { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
      @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
      @media (max-width: 820px) {
        aside { box-shadow: ${T.shadowLg}; }
        * { -webkit-tap-highlight-color: rgba(0,0,0,0); -webkit-touch-callout: none; }
        button, a, [role="button"] { touch-action: manipulation; }
        main > div { -webkit-overflow-scrolling: touch; }
        body { overscroll-behavior: contain; }
      }
    `}</style>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   DASHBOARD VIEW WRAPPER
   ════════════════════════════════════════════════════════════════════════ */
function DashboardView({
  session,
  data, fileName, loading, error,
  onLoadSample, onLoadSample2, onLoadSample3, onLoadSample4,   // ← ADD onLoadSample4
  onUpload, onAskAI, onBackToChat, onReset,
  onToggleSidebar, isMobile,
}) {
  const [newData, setNewData] = useState(data);
  useEffect(() => { setNewData(data); }, [data]);
//   useEffect(() => {
//     if(session?.session_id) {
//     const isHCCB =  session?.sheet_names.some(n => /vol\s*ytd\s*growth/i.test(n));
//       if(isHCCB){
// onLoadSample();
//       }else{   setNewData({
//     "cboData": [],
//     "catData": [],
//     "months": [],
//     "momData":{},
//     "momCat": {},
//     "totals": { 
//     },
//     "isHCCB": false
// });
//       }
//     }
//   }, [session]);
 console.log(fileName,newData,session?.session_id);  
  return (
    <>
      <header style={{
        padding: "12px 20px", background: T.surface,
        borderBottom: `1px solid ${T.border}`,
        display: "flex", alignItems: "center", gap: 10, flexShrink: 0,
      }}>
        <button onClick={onToggleSidebar} style={iconBtn} aria-label="Toggle sidebar">
          <Icon name="sidebar" size={16} color={T.textMid}/>
        </button>
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <div style={{
            width: 30, height: 30, borderRadius: 8, flexShrink: 0,
            background: "linear-gradient(135deg, #E8001A, #B50015)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Icon name="grid" size={14} color="#fff"/>
          </div>
          <div style={{ overflow: "hidden", minWidth: 0, flex: 1 }}>
            <div style={{
              fontSize: 14, fontWeight: 600, color: T.text,
              fontFamily: T.sans, letterSpacing: "-0.2px",
            }}>Dashboard</div>
            <div style={{
              fontSize: 11, color: T.textDim, fontFamily: T.serif,
              fontStyle: "italic", marginTop: 1,
            }}>
              {data ? `${data.cboData?.length || 0} segments`
                : "Visual analytics for spreadsheets"}
            </div>
          </div>
        </div>
        <button onClick={onBackToChat} style={{
          padding: "7px 14px", borderRadius: 20, gap: 7, fontSize: 12.5,
          color: T.textMid, background: T.bg,
          border: `1px solid ${T.border}`,
          cursor: "pointer", display: "inline-flex",
          alignItems: "center", fontFamily: T.sans, fontWeight: 600,
        }}>
          <Icon name="chat" size={13}/>
          {!isMobile && "Back to chat"}
        </button>
      </header>

      <div style={{ flex: 1, overflowY: "auto", background:   newData ? "#FAFAFA" : T.bg }}>
      {newData && (session?.session_id || fileName)? (
  <Dashboard
    session={session}
    data={newData}
    fileName={fileName}
    onReset={onReset}
    onAskAI={onAskAI}
  />
) : (
      <DashboardEmptyState
        onLoadSample3={onLoadSample3}
        onLoadSample={onLoadSample}
        onLoadSample2={onLoadSample2}
        onLoadSample4={onLoadSample4}  // ← ADD onLoadSample4
        onUpload={onUpload}
        loading={loading}
        error={error}
      />
)}
      </div>
    </>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   CHAT VIEW — original chat experience
   ════════════════════════════════════════════════════════════════════════ */
function ChatView({chatScrollRef,
  session, messages, input, setInput,
  uploading, streaming, dragOver, setDragOver, onDrop,
  error, setError, clarifier, setClarifier,
  attemptSend, sendMessage, stopStreaming, onKeyDown,
  fileRef, onFileChange, inputRef, bottomRef,
  tool, setTool, bing, setBing, koine, setKoine,
  recording, toggleMic,
  sidebarOpen, setSidebarOpen, isMobile, user, token, activeThread,
  showDashboardToggle, onOpenDashboard, onOpenViz,
}) {
  return (
    <>
      <header style={{
        padding: "12px 20px", background: T.surface,
        borderBottom: `1px solid ${T.border}`,
        display: "flex", alignItems: "center", gap: 10, flexShrink: 0,
        backdropFilter: "blur(6px)",
      }}>
        <button onClick={() => setSidebarOpen(o => !o)} style={iconBtn} aria-label="Toggle sidebar">
          <Icon name="sidebar" size={16} color={T.textMid}/>
        </button>
        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, overflow: "hidden", minWidth: 0 }}>
          {session?.file_name ? (
            <>
              <div style={{
                width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                background: T.accentSoft, display: "flex",
                alignItems: "center", justifyContent: "center",
              }}>
                <Icon name="file" size={14} color={T.accent}/>
              </div>
              <div style={{ overflow: "hidden", minWidth: 0, flex: 1 }}>
                <div style={{
                  fontSize: 14, fontWeight: 600, color: T.text,
                  overflow: "hidden", textOverflow: "ellipsis",
                  whiteSpace: "nowrap", fontFamily: T.sans, letterSpacing: "-0.2px",
                }}>{session.file_name}</div>
                <div style={{ display: "flex", gap: 6, marginTop: 2, flexWrap: "wrap" }}>
                  <span style={metaTag}>{session.file_kind}</span>
                  {session.sheet_names?.length > 1 && (
                    <span style={{ ...metaTag, color: T.accent, background: T.accentSoft }}>
                      {session.sheet_names.length} sheets
                    </span>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div>
              <div style={{
                fontSize: 14, fontWeight: 600, color: T.text,
                fontFamily: T.serif, letterSpacing: "-0.3px",
              }}>
                {messages.length > 0 ? "Conversation" : "New conversation"}
              </div>
              <div style={{
                fontSize: 11, color: T.textDim, fontFamily: T.serif,
                fontStyle: "italic", marginTop: 1,
              }}>
                {messages.length > 0 ? `${messages.length} messages` : "ready when you are"}
              </div>
            </div>
          )}
        </div>

        {/* Top-bar Dashboard toggle when file is spreadsheet */}
        {showDashboardToggle && (
          <button onClick={onOpenDashboard} title="Open Dashboard"
            style={{
              padding: "7px 14px", borderRadius: 20, gap: 7, fontSize: 12.5,
              color: "#fff", background: "linear-gradient(135deg, #E8001A, #B50015)",
              border: `1px solid #B50015`,
              cursor: "pointer", display: "inline-flex",
              alignItems: "center", fontFamily: T.sans, fontWeight: 600,
              boxShadow: T.shadow,
            }}>
            <Icon name="grid" size={13}/>
            {!isMobile && "Dashboard"}
          </button>
        )}

        {session?.file_kind === "text" && (
          <button onClick={onOpenViz} title="Knowledge graph & mindmap" style={{
            padding: "7px 14px", borderRadius: 20, gap: 7, fontSize: 12.5,
            color: T.accent, background: T.accentSoft, border: `1px solid ${T.accent}`,
            cursor: "pointer", display: "inline-flex",
            alignItems: "center", fontFamily: T.sans, fontWeight: 600,
          }}>
            <Icon name="network" size={13}/>
            {!isMobile && "Insight"}
          </button>
        )}
      </header>

      <div style={{
          flex: 1, overflowY: "auto", display: "flex", flexDirection: "column",
          background: T.bg, position: "relative",
        }}
         ref={chatScrollRef}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}>
        {dragOver && (
          <div style={{
            position: "absolute", inset: 0, background: "rgba(251,233,227,0.96)",
            zIndex: 40, display: "flex", alignItems: "center", justifyContent: "center",
            border: `3px dashed ${T.accent}`, margin: 12, borderRadius: T.radius,
            pointerEvents: "none",
          }}>
            <div style={{ textAlign: "center" }}>
              <Icon name="paperclip" size={48} color={T.accent}/>
              <div style={{
                marginTop: 12, fontSize: 20, fontWeight: 600, color: T.accent,
                fontFamily: T.serif, letterSpacing: "-0.3px",
              }}>Drop to upload</div>
              <div style={{
                fontSize: 12.5, color: T.textMid, marginTop: 4,
                fontFamily: T.mono, letterSpacing: "0.3px",
              }}>{SUPPORTED_EXT.join(" · ")}</div>
            </div>
          </div>
        )}

        {messages.length === 0 && !clarifier ? (
          <EmptyState userName={user?.name} hasSession={!!session?.file_name}
            onSuggestion={(s) => { setInput(s); setTimeout(() => attemptSend(s), 50); }}/>
        ) : (
          <div style={{
            maxWidth: 820, width: "100%", margin: "0 auto",
            padding: isMobile ? "16px 12px 24px" : "24px 20px 40px",
          }}>
            {messages.map((m, idx) => (
              <MessageRow key={m.id || idx} m={m} token={token}
                threadId={session?.session_id}
                prevUserQ={messages[idx - 1]?.role === "user" ? messages[idx - 1].content : ""}/>
            ))}
            {clarifier && (
              <div style={{ margin: "4px 0 0 42px" }}>
                <Clarifier
                  data={clarifier}
                  onAnswer={(v) => { setClarifier(null); sendMessage(v); }}
                  onSkip={() => { const q = clarifier.originalQuery; setClarifier(null); sendMessage(q); }}
                />
              </div>
            )}
            <div ref={bottomRef}/>
          </div>
        )}
      </div>

      {error && (
        <div style={{
          margin: "0 20px 8px", background: "#fdf0ea",
          border: `1px solid ${T.accent}`, borderRadius: T.radiusSm,
          padding: "10px 14px", fontSize: 12.5, color: T.accentDark,
          display: "flex", alignItems: "center", gap: 10, fontFamily: T.sans,
          animation: "fadeSlide 0.2s",
        }}>
          <span>⚠ {error}</span>
          <button onClick={() => setError(null)} style={{
            marginLeft: "auto", background: "none", border: "none",
            cursor: "pointer", color: T.accent,
          }}><Icon name="x" size={12}/></button>
        </div>
      )}

      <Composer
        input={input} setInput={setInput} streaming={streaming}
        attemptSend={attemptSend} stopStreaming={stopStreaming}
        onKeyDown={onKeyDown} inputRef={inputRef}
        fileRef={fileRef} onFileChange={onFileChange}
        uploading={uploading}
        tool={tool} setTool={setTool} bing={bing} setBing={setBing}
        koine={koine} setKoine={setKoine}
        recording={recording} toggleMic={toggleMic}
        isMobile={isMobile} session={session}
      />
    </>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   COMPOSER — extracted so it can be reused in drawer
   ════════════════════════════════════════════════════════════════════════ */
function Composer({
  input, setInput, streaming, attemptSend, stopStreaming, onKeyDown, inputRef,
  fileRef, onFileChange, uploading,
  tool, setTool, bing, setBing, koine, setKoine,
  recording, toggleMic, isMobile, session,
  compact = false,
}) {
  return (
    <div style={{
      padding: isMobile ? "0 10px calc(10px + env(safe-area-inset-bottom, 0px))"
                        : "0 16px 16px",
      background: T.bg, flexShrink: 0,
      borderTop: isMobile ? `1px solid ${T.border}` : "1px solid transparent",
    }}>
      <div style={{
        maxWidth: 820, width: "100%", margin: "0 auto",
        background: T.surface,
        border: `1px solid ${isMobile ? T.border : T.borderStrong}`,
        borderRadius: isMobile ? T.radius : T.radiusLg,
        padding: isMobile ? 8 : 12,
        boxShadow: isMobile ? T.shadow : T.shadowMd,
        marginTop: isMobile ? 8 : 0,
      }}>
        <div style={{
          display: "flex", alignItems: "center",
          gap: isMobile ? 6 : 8,
          padding: isMobile ? "0 2px 6px" : "0 2px 10px",
          borderBottom: `1px dashed ${T.border}`,
          marginBottom: isMobile ? 6 : 10, flexWrap: "wrap",
        }}>
          <Toolbox tool={tool} setTool={setTool} bing={bing} setBing={setBing} koine={koine} setKoine={setKoine}/>
          {fileRef && (
            <>
              <input ref={fileRef} type="file" accept={SUPPORTED_EXT.join(",")}
                style={{ display: "none" }} onChange={onFileChange}/>
              <button onClick={() => fileRef.current?.click()} title="Attach file"
                disabled={uploading || koine} style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "6px 12px", borderRadius: 20,
                  border: `1px solid ${T.border}`, background: T.surface,
                  color: T.textMid, fontSize: 12, fontWeight: 600, cursor: "pointer",
                  fontFamily: T.sans, transition: "all 0.15s",
                  opacity: koine ? 0.5 : 1, pointerEvents: koine ? "none" : "auto",
                }}>
                {uploading ? <Spinner size={11} color={T.textMid}/> : <Icon name="paperclip" size={12}/>}
                {uploading ? "Uploading…" : (isMobile ? "" : "Attach")}
              </button>
            </>
          )}
          {session?.file_name && (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              fontSize: isMobile ? 10.5 : 11.5,
              color: T.accent, padding: "4px 10px", background: T.accentSoft,
              borderRadius: 20, border: `1px solid ${T.border}`, marginLeft: 4,
              maxWidth: isMobile ? 130 : 240,
              overflow: "hidden", textOverflow: "ellipsis",
              whiteSpace: "nowrap", fontFamily: T.sans, fontWeight: 500,
            }}>
              <Icon name="file" size={10}/> {session.file_name}
            </span>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "flex-end", gap: isMobile ? 6 : 8 }}>
          <textarea
            ref={inputRef} rows={1}
            style={{
              flex: 1, border: "none", outline: "none", resize: "none",
              fontFamily: T.sans,
              fontSize: isMobile ? 15 : 14.5,
              lineHeight: 1.45,
              background: "transparent", color: T.text,
              padding: isMobile ? "6px 4px" : "8px 6px",
              maxHeight: isMobile ? 120 : 200,
              minHeight: isMobile ? 24 : 28,
            }}
            placeholder={session?.file_name
              ? (isMobile ? "Ask about your file…" : "Ask anything about your file…")
              : (isMobile ? "Message…" : "Message KOgnos AI…")}
            value={input} disabled={streaming}
            onChange={e => {
              setInput(e.target.value);
              e.target.style.height = "auto";
              const cap = isMobile ? 120 : 200;
              e.target.style.height = Math.min(cap, e.target.scrollHeight) + "px";
            }}
            onKeyDown={onKeyDown}/>
          <button onClick={toggleMic} title="Voice input" style={{
            ...iconBtn,
            width: isMobile ? 34 : 36, height: isMobile ? 34 : 36,
            background: recording ? T.accent : T.bg,
            color: recording ? "#fff" : T.textMid,
            border: `1px solid ${recording ? T.accent : T.border}`,
          }}>
            <Icon name={recording ? "micOff" : "mic"} size={isMobile ? 13 : 14}
              color={recording ? "#fff" : T.textMid}/>
          </button>
          {streaming ? (
            <button onClick={stopStreaming} title="Stop" style={{
              ...iconBtn,
              width: isMobile ? 34 : 36, height: isMobile ? 34 : 36,
              background: T.accent, color: "#fff", border: `1px solid ${T.accent}`,
            }}><Icon name="stop" size={isMobile ? 12 : 13} color="#fff"/></button>
          ) : (
            <button onClick={() => attemptSend()} disabled={!input.trim()} title="Send"
              style={{
                ...iconBtn,
                width: isMobile ? 34 : 36, height: isMobile ? 34 : 36,
                background: input.trim()
                  ? `linear-gradient(135deg, ${T.accent}, ${T.accentDark})`
                  : T.border,
                color: "#fff",
                border: `1px solid ${input.trim() ? T.accent : T.border}`,
                cursor: input.trim() ? "pointer" : "not-allowed",
                boxShadow: input.trim() ? T.shadow : "none",
              }}>
              <Icon name="send" size={isMobile ? 13 : 14} color="#fff"/>
            </button>
          )}
        </div>
      </div>
      {!isMobile && !compact && (
        <div style={{
          textAlign: "center", fontSize: 11, color: T.textDim,
          marginTop: 10, fontFamily: T.serif, fontStyle: "italic",
        }}>
          KOgnos may make mistakes — verify important info. Press ⏎ to send, ⇧⏎ for newline.
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   CHAT DRAWER — opens from Dashboard's "Ask AI". Uses the SAME MessageRow,
   streaming, clarifier, feedback, sources, etc. as the main chat.
   ════════════════════════════════════════════════════════════════════════ */
function ChatDrawer({
  onClose, session, messages, input, setInput,
  streaming, error, setError, clarifier, setClarifier,
  attemptSend, sendMessage, stopStreaming, onKeyDown,
  tool, setTool, bing, setBing, koine, setKoine,
  token, activeThread, dashFileName,
}) {
  const drawerInputRef = useRef(null);
  const drawerBottomRef = useRef(null);
const drawerScrollRef = useRef(null);
const drawerUserScrolledRef = useRef(false);

useEffect(() => {
  const el = drawerScrollRef.current;
  if (!el) return;
  const onScroll = () => {
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    drawerUserScrolledRef.current = distFromBottom > 120;
  };
  el.addEventListener("scroll", onScroll, { passive: true });
  return () => el.removeEventListener("scroll", onScroll);
}, []);

useEffect(() => {
  if (!drawerUserScrolledRef.current) {
    drawerBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }
}, [messages]);
  useEffect(() => { setTimeout(() => drawerInputRef.current?.focus(), 150); }, []);

  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(30,25,20,0.4)",
        zIndex: 60, backdropFilter: "blur(2px)", animation: "fadeIn 0.2s",
      }}/>
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: "min(540px, 96vw)",
        background: T.bg, borderLeft: `1px solid ${T.border}`,
        boxShadow: "-16px 0 40px rgba(60,50,30,0.18)", zIndex: 61,
        display: "flex", flexDirection: "column", animation: "slideInRight 0.25s",
      }}>
        <div style={{
          padding: "14px 18px", borderBottom: `1px solid ${T.border}`,
          display: "flex", alignItems: "center", gap: 10,
          background: `linear-gradient(135deg, ${T.bgAlt}, ${T.sidebar})`, flexShrink: 0,
        }}>
          <div style={{
            width: 30, height: 30, borderRadius: 8,
            background: `linear-gradient(135deg, ${T.accent}, ${T.accentDark})`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Icon name="sparkles" size={14} color="#fff"/>
          </div>
          <div style={{ flex: 1, overflow: "hidden", minWidth: 0 }}>
            <div style={{
              fontFamily: T.serif, fontWeight: 600, fontSize: 15, color: T.text, letterSpacing: "-0.3px",
            }}>Ask KOgnos AI</div>
            <div style={{
              fontFamily: T.serif, fontStyle: "italic", fontSize: 11, color: T.textMid,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {session?.file_name
                ? `About ${session.file_name}`
                : dashFileName
                  ? `Upload ${dashFileName} to chat with full RAG`
                  : "Chat about your dashboard data"}
            </div>
          </div>
          <button onClick={onClose} style={{
            width: 30, height: 30, borderRadius: 8,
            background: T.surface, border: `1px solid ${T.border}`, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}><Icon name="x" size={13} color={T.textMid}/></button>
        </div>

        <div ref={drawerScrollRef} style={{ flex: 1, overflowY: "auto", padding: "16px 14px" }}>
          {!session?.file_name && dashFileName && (
            <div style={{
              padding: "10px 14px", background: T.accentSoft,
              border: `1px solid ${T.border}`, borderRadius: T.radiusSm,
              fontSize: 12, color: T.accentDark, fontFamily: T.serif,
              fontStyle: "italic", marginBottom: 14, lineHeight: 1.55,
            }}>
              💡 Your dashboard was built from <strong>{dashFileName}</strong> (parsed in-browser).
              To enable full RAG search and pandas analysis, also upload the file via the chat composer below.
            </div>
          )}
          {messages.length === 0 && !clarifier ? (
            <div style={{
              padding: 20, textAlign: "center", color: T.textDim,
              fontFamily: T.serif, fontStyle: "italic", fontSize: 14,
            }}>
              Ask anything about your data — same chat as the main view, just popped out here.
            </div>
          ) : (
            <>
              {messages.map((m, idx) => (
                <MessageRow key={m.id || idx} m={m} token={token}
                  threadId={session?.session_id || activeThread}
                  prevUserQ={messages[idx - 1]?.role === "user" ? messages[idx - 1].content : ""}/>
              ))}
              {clarifier && (
                <Clarifier
                  data={clarifier}
                  onAnswer={(v) => { setClarifier(null); sendMessage(v); }}
                  onSkip={() => { const q = clarifier.originalQuery; setClarifier(null); sendMessage(q); }}
                />
              )}
              <div ref={drawerBottomRef}/>
            </>
          )}
        </div>

        {error && (
          <div style={{
            margin: "0 14px 6px", background: "#fdf0ea",
            border: `1px solid ${T.accent}`, borderRadius: T.radiusSm,
            padding: "8px 12px", fontSize: 12, color: T.accentDark,
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <span>⚠ {error}</span>
            <button onClick={() => setError(null)} style={{
              marginLeft: "auto", background: "none", border: "none",
              cursor: "pointer", color: T.accent,
            }}><Icon name="x" size={11}/></button>
          </div>
        )}

        <Composer
          input={input} setInput={setInput} streaming={streaming}
          attemptSend={attemptSend} stopStreaming={stopStreaming}
          onKeyDown={onKeyDown} inputRef={drawerInputRef}
          fileRef={null} onFileChange={() => {}} uploading={false}
          tool={tool} setTool={setTool} bing={bing} setBing={setBing}
          koine={koine} setKoine={setKoine}
          recording={false} toggleMic={() => {}}
          isMobile={false} session={session} compact
        />
      </div>
    </>
  );
}